import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const UpdateEmailDTO = z.object({ email: z.string() }).loose()
const partner_ids = z.union([z.array(z.string()), z.null()]).optional()
const partner_id = z.union([z.string(), z.null()]).optional()
const UserSchema = z
  .object({
    login: z.string(),
    is_active: z.boolean(),
    auth_provider: z.union([z.string(), z.null()]).optional(),
    auth_provider_record_id: z.union([z.string(), z.null()]).optional(),
    id: z.number().int(),
    organization_id: z.string().uuid(),
    client_id: z.string(),
  })
  .loose()
const HTTPExceptionSchema = z.object({ detail: z.string() }).loose()
const ValidationError = z
  .object({
    loc: z.array(z.union([z.string(), z.number()])),
    msg: z.string(),
    type: z.string(),
    input: z.unknown().optional(),
    ctx: z.object({}).partial().loose().optional(),
  })
  .loose()
const HTTPValidationError = z
  .object({ detail: z.array(ValidationError) })
  .partial()
  .loose()
const UserDiscoveryDTO = z
  .object({ email: z.string(), organization_id: z.string().uuid() })
  .loose()
const UserDiscoverySchema = z.object({ is_discovered: z.boolean() }).loose()
const OrgClientSchema = z
  .object({
    name: z.string(),
    id: z.string(),
    is_admin: z.boolean(),
    is_primary: z.boolean(),
    organization_id: z.string().uuid(),
  })
  .loose()
const OrgSchema = z
  .object({ name: z.string(), id: z.string().uuid(), clients: z.array(OrgClientSchema).optional() })
  .loose()
const IdentityInfoSchema = z
  .object({
    primary_client_id: z.string(),
    organization_id: z.string().uuid(),
    id: z.number().int(),
    is_active: z.boolean(),
    scopes: z.record(z.string(), z.object({}).partial().loose()),
  })
  .loose()
const FileAccessFlagValue = z.enum(['disabled', 'read', 'write'])
const ClientFileStatus = z.enum(['active', 'quarantined'])
const ClientFileSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int(),
    uploadedAt: z.iso.datetime({ local: true }),
    uploadedByUserId: z.number().int(),
    uploadedByName: z.union([z.string(), z.null()]),
    uploadedByAshUser: z.boolean(),
    status: ClientFileStatus,
    canDelete: z.boolean(),
  })
  .loose()
const PartnerReportFileSchema = z
  .object({
    name: z.string(),
    contentType: z.union([z.string(), z.null()]),
    sizeBytes: z.number().int(),
    updatedAt: z.iso.datetime({ local: true }),
  })
  .loose()
const PartnerReportsSchema = z
  .object({ available: z.boolean(), files: z.array(PartnerReportFileSchema) })
  .loose()
const ClientFilesResponseSchema = z
  .object({
    access: FileAccessFlagValue,
    uploadedFiles: z.array(ClientFileSchema),
    uploadedFilesTotal: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    reports: PartnerReportsSchema,
  })
  .loose()
const Body_upload_client_file_v2_clients_files_post = z.object({ file: z.instanceof(Blob) }).loose()
const SignedFileUrlSchema = z
  .object({ url: z.string(), expiresAt: z.iso.datetime({ local: true }) })
  .loose()
const ClientFileQuarantinedErrorSchema = z
  .object({ detail: z.string(), portal_error_code: z.string() })
  .loose()
const full_history = z.union([z.boolean(), z.null()]).optional()
const KitOrderLabInfoSchema = z
  .object({
    prettyName: z.union([z.string(), z.null()]),
    address: z.union([z.string(), z.null()]),
    city: z.union([z.string(), z.null()]),
    state: z.union([z.string(), z.null()]),
    zip: z.union([z.string(), z.null()]),
    cliaId: z.union([z.string(), z.null()]),
    director: z.union([z.string(), z.null()]),
    capNumber: z.union([z.string(), z.null()]),
  })
  .partial()
  .loose()
const KitOrderResultLineSchema = z
  .object({
    abnormalRangeName: z.union([z.string(), z.null()]).optional(),
    ashCode: z.union([z.string(), z.null()]).optional(),
    collectionDate: z.union([z.string(), z.null()]).optional(),
    correctionNumber: z.number().int().optional().default(0),
    resultDate: z.union([z.string(), z.null()]).optional(),
    inconclusiveRangeName: z.union([z.string(), z.null()]).optional(),
    inconclusiveReferenceRange: z.union([z.string(), z.null()]).optional(),
    normalRangeName: z.union([z.string(), z.null()]).optional(),
    note: z.union([z.string(), z.null()]).optional(),
    prettyName: z.string(),
    pretty_test_name: z.string(),
    referenceRange: z.union([z.string(), z.null()]).optional(),
    ranges: z.object({}).partial().loose(),
    resultValue: z.string(),
    status: z.union([z.string(), z.null()]).optional(),
    testName: z.string(),
    testType: z.union([z.string(), z.null()]).optional(),
    units: z.union([z.string(), z.null()]).optional(),
    isAbnormal: z.union([z.boolean(), z.null()]).optional(),
    isRejected: z.boolean().optional().default(false),
    isMultiPhysiological: z.boolean().optional().default(false),
  })
  .loose()
const KitOrderResultLineWithCorrectionSchema = z
  .object({
    abnormalRangeName: z.union([z.string(), z.null()]).optional(),
    ashCode: z.union([z.string(), z.null()]).optional(),
    collectionDate: z.union([z.string(), z.null()]).optional(),
    correctionNumber: z.number().int().optional().default(0),
    resultDate: z.union([z.string(), z.null()]).optional(),
    inconclusiveRangeName: z.union([z.string(), z.null()]).optional(),
    inconclusiveReferenceRange: z.union([z.string(), z.null()]).optional(),
    normalRangeName: z.union([z.string(), z.null()]).optional(),
    note: z.union([z.string(), z.null()]).optional(),
    prettyName: z.string(),
    pretty_test_name: z.string(),
    referenceRange: z.union([z.string(), z.null()]).optional(),
    ranges: z.object({}).partial().loose(),
    resultValue: z.string(),
    status: z.union([z.string(), z.null()]).optional(),
    testName: z.string(),
    testType: z.union([z.string(), z.null()]).optional(),
    units: z.union([z.string(), z.null()]).optional(),
    isAbnormal: z.union([z.boolean(), z.null()]).optional(),
    isRejected: z.boolean().optional().default(false),
    isMultiPhysiological: z.boolean().optional().default(false),
    correctionHistory: z.union([z.array(KitOrderResultLineSchema), z.null()]).optional(),
  })
  .loose()
const ProviderInfoOutput = z
  .object({ firstName: z.string().max(45), lastName: z.string().max(45), npi: z.string().max(10) })
  .loose()
const ResultsByKitIdSchema = z
  .object({
    kitId: z.string(),
    partnerId: z.string(),
    labName: z.union([z.string(), z.null()]).optional(),
    labInfo: z.union([KitOrderLabInfoSchema, z.null()]).optional(),
    pdf: z.union([z.string(), z.null()]).optional(),
    pdfEntities: z.union([z.array(z.object({}).partial().loose()), z.null()]).optional(),
    pdfs: z.union([z.array(z.union([z.string(), z.null()])), z.null()]).optional(),
    results: z.array(KitOrderResultLineWithCorrectionSchema),
    dateReported: z.union([z.string(), z.null()]).optional(),
    providerInfo: ProviderInfoOutput,
    inboundTrackingUrl: z.union([z.string(), z.null()]).optional(),
    outboundTrackingUrl: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body =
  z.union([z.array(z.string()), z.null()])
const OderDetailsSectionSchema = z
  .object({
    sku: z.union([z.string(), z.null()]),
    pretty_sku_name: z.union([z.string(), z.null()]),
    patient_first_name: z.union([z.string(), z.null()]),
    patient_last_name: z.union([z.string(), z.null()]),
    patient_email: z.union([z.string(), z.null()]),
  })
  .loose()
const ShippingInfoSchema = z
  .object({
    first_name: z.union([z.string(), z.null()]),
    last_name: z.union([z.string(), z.null()]),
    email: z.union([z.string(), z.null()]).optional(),
    phone: z.union([z.string(), z.null()]).optional(),
    state: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const TrackingInfoSchema = z
  .object({
    outbound_tracking_id: z.union([z.string(), z.null()]),
    inbound_tracking_id: z.union([z.string(), z.null()]),
    outbound_shipping_method: z.union([z.string(), z.null()]),
    inbound_shipping_method: z.union([z.string(), z.null()]),
    outbound_tracking_url: z.union([z.string(), z.null()]).optional(),
    inbound_tracking_url: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const KitOrderResultCodeSchema = z
  .object({
    code: z.string(),
    prettyName: z.union([z.string(), z.null()]).optional(),
    pretty_test_name: z.string(),
    optionality_marker: z.number().int().optional().default(0),
  })
  .loose()
const KitOrderCodeSchema = z
  .object({ ash_code: z.string(), result_codes: z.array(KitOrderResultCodeSchema).optional() })
  .loose()
const SingleEventSchema = z
  .object({ event: z.string(), timestamp: z.iso.datetime({ local: true }) })
  .loose()
const OrderAdditionalFieldEntrySchema = z
  .object({ value: z.union([z.string(), z.null()]).optional(), contains_phi: z.boolean() })
  .loose()
const ResultRejectionInfoSchema = z
  .object({
    test_name: z.string(),
    reason: z.string(),
    is_recoverable: z.union([z.boolean(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const AoeQuestionOptionSchema = z.object({ value: z.string(), label: z.string() }).loose()
const AoeQuestionAnswerSchema = z
  .object({
    question_id: z.string(),
    label: z.union([z.string(), z.null()]).optional(),
    type: z.union([z.string(), z.null()]).optional(),
    answer: z.string(),
    options: z.union([z.array(AoeQuestionOptionSchema), z.null()]).optional(),
    validation_rules: z.union([z.object({}).partial().loose(), z.null()]).optional(),
  })
  .loose()
const SampleCollectionTypeSchema = z
  .object({ id: z.number().int(), slug: z.string(), pretty_name: z.string() })
  .loose()
const SampleCollectionConfigurationSchema = z
  .object({
    sample_types: z.array(SampleCollectionTypeSchema),
    instruction_id: z.union([z.string(), z.null()]).optional(),
    unresolved_ash_host_codes: z.array(z.string()).optional(),
  })
  .loose()
const OrderDetailsWithShippingAndResultsSchema = z
  .object({
    user_id: z.union([z.number(), z.null()]),
    kit_id: z.string(),
    order_id: z.union([z.string(), z.null()]),
    order_placement_note: z.union([z.string(), z.null()]).optional(),
    automatically_registered: z.boolean().optional().default(false),
    order_type: z.array(z.string()).optional(),
    kit_registration_date: z.union([z.string(), z.null()]),
    kit_status: z.union([z.string(), z.null()]),
    order_details: z.union([OderDetailsSectionSchema, z.null()]),
    shipping_info: z.union([ShippingInfoSchema, z.null()]),
    tracking_info: z.union([TrackingInfoSchema, z.null()]),
    results: z.union([ResultsByKitIdSchema, z.null()]).optional(),
    codes: z.array(KitOrderCodeSchema),
    events: z.array(SingleEventSchema),
    additional_fields: z.union([z.record(z.string(), OrderAdditionalFieldEntrySchema), z.null()]).optional(),
    shown_extra_fields: z.union([z.object({}).partial().loose(), z.null()]).optional(),
    hidden_fields: z.union([z.array(z.string()), z.null()]).optional(),
    rejection_reasons: z.array(ResultRejectionInfoSchema).optional(),
    aoe_questions: z.union([z.array(AoeQuestionAnswerSchema), z.null()]).optional(),
    sample_collection_configuration: z
      .union([SampleCollectionConfigurationSchema, z.null()])
      .optional(),
  })
  .loose()
const PatientProvidedPCPSchema = z
  .object({
    pcp_name: z.union([z.string(), z.null()]),
    pcp_fax: z.union([z.string(), z.null()]),
    pcp_address1: z.union([z.string(), z.null()]),
    pcp_address2: z.union([z.string(), z.null()]),
    pcp_city: z.union([z.string(), z.null()]),
    pcp_state: z.union([z.string(), z.null()]),
    pcp_zip: z.union([z.string(), z.null()]),
    send_fax_results_to_pcp: z.boolean().default(false),
  })
  .partial()
  .loose()
const OrderPrimaryCareProviderAddressSchema = z
  .object({
    address1: z.string(),
    address2: z.union([z.string(), z.null()]).optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  })
  .loose()
const OrderPrimaryCareProviderSchema = z
  .object({
    name: z.union([z.string(), z.null()]),
    npi: z.union([z.string(), z.null()]),
    address: z.union([OrderPrimaryCareProviderAddressSchema, z.null()]),
    fax: z.union([z.string(), z.null()]),
  })
  .partial()
  .loose()
const ClientOrderPCPInfoSchema = z
  .object({
    kit_id: z.string(),
    need_pcp_help: z.union([z.boolean(), z.null()]).optional(),
    patient_provided_pcp: z.union([PatientProvidedPCPSchema, z.null()]).optional(),
    order_provided_pcp: z.union([OrderPrimaryCareProviderSchema, z.null()]).optional(),
  })
  .loose()
const EligibilityQuestionnaireResponseSchema = z
  .object({
    questionId: z.string(),
    question: z.string(),
    patientResponse: z.string(),
    eligibleForKitRegistration: z.boolean(),
  })
  .loose()
const EligibilityQuestionnaireResponsesSchema = z
  .object({ responses: z.array(EligibilityQuestionnaireResponseSchema) })
  .loose()
const OrderAPIOrderEvents = z.enum([
  'order-received',
  'order-fulfilled',
  'kit-fulfillment-canceled',
  'kit-returned-to-sender',
  'kit-in-transit-to-patient',
  'kit-out-for-patient-delivery',
  'kit-delivered-to-patient',
  'kit-registered',
  'kit-in-transit-to-lab',
  'kit-out-for-lab-delivery',
  'provider-assigned',
  'provider-approval-completed',
  'test-samples-received',
  'kit-accessioned',
  'kit-issue',
  'kit-issue-resolved',
  'kit-rejected',
  'results-ready',
  'result-fax-successful',
  'result-fax-recipient-failure',
  'results-mailed-pcp',
])
const ShippingAddressDTO = z
  .object({
    address1: z.string().min(1).max(64),
    address2: z.union([z.string().max(64), z.null()]).optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.union([z.string(), z.null()]).optional(),
    phone: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const PatientAddressDTO = z
  .object({
    address1: z.string().min(1).max(64),
    address2: z.union([z.string().max(64), z.null()]).optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  })
  .loose()
const PatientInfoDTO = z
  .object({
    firstName: z.string(),
    lastName: z.string(),
    sexAtBirth: z.string(),
    dob: z.string(),
    phone: z.string(),
    email: z.union([z.string(), z.null()]).optional(),
    middleInitial: z.union([z.string(), z.null()]).optional(),
    race: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const ProviderInfoDTO = z
  .object({ firstName: z.string(), lastName: z.string(), npi: z.string() })
  .loose()
const FulfillmentDTO = z
  .object({
    ashOutboundShippingMethod: z.union([z.string(), z.null()]),
    ashInboundShippingMethod: z.union([z.string(), z.null()]),
    outboundTrackingId: z.union([z.string(), z.null()]),
    inboundTrackingId: z.union([z.string(), z.null()]),
    fulfillmentPartner: z.union([z.string(), z.null()]),
  })
  .partial()
  .loose()
const InsuranceDTO = z
  .object({
    isPrimary: z.boolean(),
    policyId: z.string(),
    relationToInsured: z.string(),
    effectiveDate: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    dob: z.string(),
    planId: z.union([z.number(), z.null()]).optional(),
    groupId: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const DesiredTestResultDTO = z
  .object({ resultCodeId: z.number().int(), value: z.string().max(64) })
  .loose()
const SetTestOrderStatusDTO = z
  .object({
    status: OrderAPIOrderEvents,
    shipping: z.union([ShippingAddressDTO, z.null()]).optional(),
    patientAddress: z.union([PatientAddressDTO, z.null()]).optional(),
    patientInfo: z.union([PatientInfoDTO, z.null()]).optional(),
    providerInfo: z.union([ProviderInfoDTO, z.null()]).optional(),
    fulfillment: z.union([FulfillmentDTO, z.null()]).optional(),
    kitEventHistory: z
      .union([z.array(z.record(z.string(), z.iso.datetime({ local: true }))), z.null()])
      .optional(),
    insurances: z.array(InsuranceDTO).optional(),
    icdCodes: z.array(z.string()).optional(),
    isReplacement: z.boolean().optional().default(false),
    desiredResults: z.union([z.array(DesiredTestResultDTO), z.null()]).optional(),
    desiredRejectionReason: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const SetTestOrderStatusSchema = z
  .object({ status: z.string(), orderId: z.string(), kitId: z.string(), partnerId: z.string() })
  .loose()
const AddKitEventDTO = z.object({ event: OrderAPIOrderEvents }).loose()
const AddKitEventSchema = z.object({ message: z.string(), details: z.string() }).loose()
const RequestTestOrderCorrectionDTO = z
  .object({ status: OrderAPIOrderEvents, desiredResults: z.array(DesiredTestResultDTO) })
  .partial()
  .loose()
const RequestTestOrderCorrectionSchema = z
  .object({ orderId: z.string(), kitId: z.string(), partnerId: z.string() })
  .loose()
const KitValidationResponseSchema = z
  .object({
    message: z.string().optional().default('Kit validation successful'),
    kit_id: z.string(),
    partner_id: z.string(),
    sku: z.union([z.string(), z.null()]).optional(),
    kit_state_is_ny: z.boolean(),
    valid: z.boolean(),
  })
  .loose()
const ClientRegisterKitPatientInfoDTO = z
  .object({
    firstName: z.string().min(1).max(45),
    lastName: z.string().min(1).max(45),
    sexAtBirth: z.string().min(1).max(8),
    dob: z.string(),
    phone: z.string().min(1).max(16),
    email: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const ClientRegisterKitPatientAddressDTO = z
  .object({
    address1: z.string().min(1).max(64),
    address2: z.union([z.string().max(64), z.null()]).optional(),
    city: z.string().min(1).max(45),
    state: z.string().min(2).max(2),
    zip: z.string().min(1).max(10),
    country: z.string().min(1).max(16),
  })
  .loose()
const ConsentAgreementType = z.enum(['at_home_consent', 'hipaa_consent'])
const ConsentAgreementSource = z.literal('client-portal')
const ClientRegisterKitConsentAgreementDTO = z
  .object({
    agreementType: ConsentAgreementType,
    consentedAt: z.iso.datetime({ local: true }),
    agreementId: z.union([z.number(), z.null()]).optional(),
    versionId: z.union([z.number(), z.null()]).optional(),
    source: ConsentAgreementSource.optional(),
  })
  .loose()
const EligibilityQuestionnaireItemDTO = z
  .object({
    questionId: z.string().max(128),
    question: z.string().max(512),
    patientResponse: z.string().max(512),
    eligibleForKitRegistration: z.boolean(),
  })
  .loose()
const ClientRegisterKitPcpContactDTO = z
  .object({
    pcpName: z.union([z.string().min(1).max(90), z.null()]),
    pcpFax: z.union([z.string().min(1).max(16).regex(/^\d{10}$/), z.null()]),
    pcpAddress1: z.union([z.string().min(1).max(64), z.null()]),
    pcpAddress2: z.union([z.string().max(64), z.null()]),
    pcpCity: z.union([z.string().min(1).max(45), z.null()]),
    pcpState: z.union([z.string().min(2).max(2), z.null()]),
    pcpZip: z.union([z.string().min(1).max(10), z.null()]),
  })
  .partial()
  .loose()
const ClientRegisterKitPcpDTO = z
  .object({
    hasPcp: z.boolean(),
    needPcpHelp: z.union([z.boolean(), z.null()]).optional(),
    sendFaxResultsToPcp: z.union([z.boolean(), z.null()]).optional(),
    contact: z.union([ClientRegisterKitPcpContactDTO, z.null()]).optional(),
  })
  .loose()
const ClientRegisterKitDTO = z
  .object({
    kitId: z.string(),
    consent: z.boolean().optional().default(true),
    patientInfo: ClientRegisterKitPatientInfoDTO,
    patientAddress: ClientRegisterKitPatientAddressDTO,
    consentAgreements: z.array(ClientRegisterKitConsentAgreementDTO).min(1),
    eligibilityQuestionnaire: z.array(EligibilityQuestionnaireItemDTO).max(50).optional(),
    pcp: z.union([ClientRegisterKitPcpDTO, z.null()]).optional(),
  })
  .loose()
const KitRegistrationResponseSchema = z
  .object({
    message: z.string().optional().default('Kit registration successful'),
    kit_id: z.string(),
    partner_id: z.string(),
    registered: z.boolean(),
  })
  .loose()
const CustomerIoMessageCustomerIdentifierEntity = z
  .object({ cio_id: z.union([z.string(), z.null()]) })
  .partial()
  .loose()
const CustomerIoMessageMetricsEntity = z
  .object({
    delivered: z.union([z.string(), z.null()]),
    processed: z.union([z.string(), z.null()]),
    human_opened: z.union([z.string(), z.null()]),
    opened: z.union([z.string(), z.null()]),
    sent: z.union([z.string(), z.null()]),
  })
  .partial()
  .loose()
const CustomerIoMessageSchema = z
  .object({
    id: z.string(),
    cio_id: z.union([z.string(), z.null()]).optional(),
    action_id: z.union([z.string(), z.number(), z.null()]).optional(),
    broadcast_id: z.union([z.string(), z.number(), z.null()]).optional(),
    campaign_id: z.union([z.string(), z.number(), z.null()]).optional(),
    content_id: z.union([z.string(), z.number(), z.null()]).optional(),
    created: z.number().int(),
    customer_id: z.union([z.string(), z.null()]).optional(),
    customer_identifier: z.union([CustomerIoMessageCustomerIdentifierEntity, z.null()]).optional(),
    deduplicate_id: z.union([z.string(), z.null()]).optional(),
    failure_message: z.union([z.string(), z.null()]).optional(),
    forgotten: z.union([z.boolean(), z.null()]).optional(),
    metrics: CustomerIoMessageMetricsEntity,
    msg_template_id: z.union([z.number(), z.null()]).optional(),
    newsletter_id: z.union([z.number(), z.null()]).optional(),
    parent_action_id: z.union([z.string(), z.number(), z.null()]).optional(),
    recipient: z.string(),
    subject: z.string(),
    transactional_message_id: z.union([z.number(), z.null()]).optional(),
    trigger_event_id: z.union([z.string(), z.number(), z.null()]).optional(),
    type: z.string(),
  })
  .loose()
const OrderClientConfigurationSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    kit_registration_by_patient: z.boolean(),
    ash_por: z.boolean(),
  })
  .loose()
const ResultCodeType = z.enum([
  'quantitative',
  'qualitative',
  'immun',
  'umpepp',
  'sti_qualitative',
  'sti_quantitative',
  'ratio',
  'free_form',
])
const ResultCodeWithReferenceRangesSchema = z
  .object({
    id: z.number().int(),
    code: z.string(),
    name: z.string(),
    type: ResultCodeType,
    units: z.union([z.string(), z.null()]).optional(),
    reference_ranges: z.array(z.string()),
    corrupted_values: z.array(z.string()).optional(),
  })
  .loose()
const LabCodeSchema = z
  .object({
    id: z.number().int(),
    code: z.string(),
    name: z.string(),
    name_in_result: z.union([z.string(), z.null()]).optional(),
    lab_id: z.number().int(),
    result_codes: z.array(ResultCodeWithReferenceRangesSchema),
  })
  .loose()
const SkuRejectionReasonSchema = z
  .object({
    id: z.number().int(),
    lab_id: z.number().int(),
    lab_reason: z.string(),
    is_recoverable: z.boolean(),
  })
  .loose()
const SkuSchema = z
  .object({
    id: z.number().int(),
    order_sku: z.string(),
    order_sku_name: z.union([z.string(), z.null()]).optional(),
    is_ny: z.boolean(),
    order_type: z.union([z.array(z.string()), z.null()]).optional(),
    ash_host_codes: z.union([z.array(z.string()), z.null()]).optional(),
    lab_id: z.union([z.number(), z.null()]).optional(),
    results: z.union([z.array(LabCodeSchema), z.null()]).optional(),
    rejection_reasons: z.union([z.array(SkuRejectionReasonSchema), z.null()]).optional(),
    sample_collection_configuration: z
      .union([SampleCollectionConfigurationSchema, z.null()])
      .optional(),
  })
  .loose()
const PaginatedResponseSchema_SkuSchema_ = z
  .object({
    items: z.array(SkuSchema),
    page_size: z.number().int(),
    page: z.number().int().optional().default(0),
    total_pages: z.number().int().optional().default(0),
    total_items: z.number().int().optional().default(0),
  })
  .loose()
const RejectionReasonSummarySchema = z
  .object({
    reason: z.string(),
    description: z.union([z.string(), z.null()]).optional(),
    is_recoverable: z.union([z.boolean(), z.null()]).optional(),
    kit_count: z.number().int(),
  })
  .loose()
const KitRejectionsSummarySchema = z
  .object({
    total_rejected_kits: z.number().int(),
    previous_period_total_rejected_kits: z.union([z.number(), z.null()]).optional(),
    reasons: z.array(RejectionReasonSummarySchema),
  })
  .loose()
const OrderAdditionalFieldSchema = z
  .object({
    id: z.number().int(),
    partner_id: z.string(),
    field_name: z.string(),
    display_name: z.union([z.string(), z.null()]).optional(),
    mandatory: z.boolean(),
    contains_phi: z.boolean(),
  })
  .loose()
const ProviderSchema = z
  .object({
    id: z.number().int(),
    partner_id: z.string(),
    npi: z.string().min(10).max(10),
    first_name: z.string().min(1).max(45),
    last_name: z.string().min(1).max(45),
  })
  .loose()
const CancelFulfillmentInputDTO = z
  .object({
    partner_id: z.union([z.string(), z.null()]).optional(),
    partner_ids: z.array(z.string()).optional(),
    order_id: z.string(),
  })
  .loose()
const Npi = z.string()
const PrimaryCareProviderAddress = z
  .object({
    address1: z.string(),
    address2: z.union([z.string(), z.null()]).optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  })
  .loose()
const Fax = z.string()
const PrimaryCareProvider = z
  .object({
    name: z.string().min(1).max(91),
    npi: z.union([Npi, z.null()]).optional(),
    address: z.union([PrimaryCareProviderAddress, z.null()]).optional(),
    fax: z.union([Fax, z.null()]).optional(),
  })
  .loose()
const PlaceOrderAoeAnswerDTO = z
  .object({ questionId: z.string().max(100), answer: z.string() })
  .loose()
const LabBillingType = z.enum(['patient', 'insurance', 'ash'])
const InsuranceAddress = z
  .object({
    address1: z.string(),
    address2: z.union([z.string(), z.null()]).optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  })
  .loose()
const InsuranceInfo = z
  .object({
    isPrimary: z.boolean(),
    planId: z.string(),
    policyId: z.string(),
    relationToInsured: z.string(),
    effectiveDate: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    dob: z.string(),
    groupId: z.union([z.string(), z.null()]).optional(),
    icdCodes: z.array(z.string()),
    address: InsuranceAddress,
  })
  .loose()
const PlaceOrderDTO = z
  .object({
    orderId: z.union([z.string(), z.null()]).optional(),
    isTestOrder: z.boolean().optional().default(false),
    sku: z.string(),
    shipping: ShippingAddressDTO,
    patientAddress: z.union([PatientAddressDTO, z.null()]).optional(),
    patientInfo: z.union([PatientInfoDTO, z.null()]).optional(),
    providerInfo: z.union([ProviderInfoDTO, z.null()]).optional(),
    primaryCareProvider: z.union([PrimaryCareProvider, z.null()]).optional(),
    additionalFields: z.union([z.object({}).partial().loose(), z.null()]).optional(),
    aoeAnswers: z.union([z.array(PlaceOrderAoeAnswerDTO), z.null()]).optional(),
    orderPlacementNote: z.union([z.string().max(300), z.null()]).optional(),
    labBillingType: z.union([LabBillingType, z.null()]).optional(),
    insurances: z.union([z.array(InsuranceInfo), z.null()]).optional(),
  })
  .loose()
const partner_id__2 = z.union([z.string(), z.null()])
const PlacedOrderSchema = z
  .object({ kit_id: z.string(), partner_id: z.string(), order_id: z.string(), sku: z.string() })
  .loose()
const InsuranceItem = z
  .object({
    id: z.number().int(),
    name: z.string(),
    address1: z.string(),
    address2: z.union([z.string(), z.null()]).optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
    createdAt: z.iso.datetime({ local: true }),
    updatedAt: z.iso.datetime({ local: true }),
  })
  .loose()
const InsuranceListResponse = z
  .object({ items: z.union([z.array(InsuranceItem), z.null()]) })
  .partial()
  .loose()
const ContinueKitInvestigationDTO = z.object({ prompt: z.string().min(1).max(4000) }).loose()
const ClientUserProfileSchema = z
  .object({
    first_name: z.union([z.string(), z.null()]).optional(),
    last_name: z.union([z.string(), z.null()]).optional(),
    partner_id: z.string(),
    email: z.string(),
    user_id: z.union([z.number(), z.null()]).optional(),
    is_active: z.boolean(),
  })
  .loose()
const UpdateClientUserProfileDTO = z
  .object({
    first_name: z.union([z.string().max(45), z.null()]),
    last_name: z.union([z.string().max(45), z.null()]),
  })
  .partial()
  .loose()
const FieldSorting = z.enum(['asc', 'desc'])
const sort_by_name = z.union([FieldSorting, z.null()]).optional()
const ProgramDTO = z.object({ id: z.string(), name: z.string() }).loose()
const ClientUserProfileListSchema = z
  .object({
    first_name: z.union([z.string(), z.null()]).optional(),
    last_name: z.union([z.string(), z.null()]).optional(),
    partner_id: z.string(),
    email: z.string(),
    user_id: z.union([z.number(), z.null()]).optional(),
    is_active: z.boolean(),
    role: z.union([z.string(), z.null()]).optional(),
    can_be_deleted: z.boolean(),
    programs: z.union([z.array(ProgramDTO), z.null()]).optional(),
  })
  .loose()
const PaginatedResponseSchema_ClientUserProfileListSchema_ = z
  .object({
    items: z.array(ClientUserProfileListSchema),
    page_size: z.number().int(),
    page: z.number().int().optional().default(0),
    total_pages: z.number().int().optional().default(0),
    total_items: z.number().int().optional().default(0),
  })
  .loose()
const DeleteClientUserDTO = z
  .object({
    partner_id: z.union([z.string(), z.null()]).optional(),
    partner_ids: z.array(z.string()).optional(),
    email: z.string(),
  })
  .loose()
const ClientInvitationReminderSchema = z
  .object({
    first_name: z.union([z.string(), z.null()]).optional(),
    last_name: z.union([z.string(), z.null()]).optional(),
    email: z.string(),
    invited_date: z.iso.datetime({ local: true }),
    partner_ids: z.array(z.string()),
    partner_names: z.array(z.string()),
    reminder_partner_id: z.string(),
  })
  .loose()
const SendClientInvitationReminderDTO = z
  .object({
    partner_id: z.union([z.string(), z.null()]).optional(),
    partner_ids: z.array(z.string()).optional(),
    email: z.string(),
  })
  .loose()
const ClientUserMfaStatusSchema = z
  .object({ user_id: z.union([z.number(), z.null()]).optional(), mfa_configured: z.boolean() })
  .loose()
const FeatureFlagSchema = z
  .object({
    name: z.string(),
    value: z.union([
      z.string(),
      z.boolean(),
      z.number(),
      z.number(),
      z.object({}).partial().loose(),
      z.array(z.unknown()),
      z.null(),
    ]),
  })
  .loose()
const ProgramStatus = z.enum(['not-launched', 'active', 'finished'])
const PartnerSchema = z
  .object({
    partner_id: z.string(),
    partner_name: z.string(),
    client_portal_order_placement: z.boolean(),
    patient_portal: z.boolean(),
    kit_registration_by_patient: z.boolean(),
    ash_por: z.boolean(),
    feature_flags: z.array(FeatureFlagSchema),
    start_date: z.union([z.string(), z.null()]).optional(),
    end_date: z.union([z.string(), z.null()]).optional(),
    status: ProgramStatus,
  })
  .loose()
const ClientUserWithPartnersSchema = z.object({ partners: z.array(PartnerSchema) }).loose()
const ImpersonateClientUserResponseSchema = z
  .object({
    user_id: z.number().int(),
    member_id: z.string(),
    session_jwt: z.string(),
    session_token: z.string(),
  })
  .loose()
const FeatureFlagsResponseSchema = z
  .object({ feature_flags: z.array(FeatureFlagSchema) })
  .loose()
const infrastructure__schemas__user_schemas__PrimaryCareProviderSchema = z
  .object({
    id: z.string(),
    user_id: z.union([z.string(), z.null()]),
    send_fax_results_to_pcp: z.boolean(),
    pcp_name: z.union([z.string(), z.null()]),
    pcp_fax: z.union([z.string(), z.null()]),
  })
  .loose()
const GetCustomFieldInUserSchema = z.object({ field_name: z.string() }).loose()
const GetUserCustomFieldInUserSchema = z
  .object({ value: z.union([z.string(), z.null()]), field: GetCustomFieldInUserSchema })
  .loose()
const HipaaConsentInfoSchema = z
  .object({
    consented_at: z.iso.datetime({ local: true }),
    expires_at: z.iso.datetime({ local: true }),
    version_id: z.union([z.number(), z.null()]).optional(),
  })
  .loose()
const PatientUserModelExtendedSchema = z
  .object({
    address1: z.string(),
    address2: z.union([z.string(), z.null()]).optional(),
    city: z.string(),
    country: z.string(),
    date_user_joined: z.iso.datetime({ local: true }),
    dob: z.string(),
    email: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    gender_identity: z.union([z.string(), z.null()]).optional(),
    partner_id: z.string(),
    phone: z.string(),
    preferred_name: z.union([z.string(), z.null()]).optional(),
    sex_assigned_at_birth: z.string(),
    state: z.string(),
    user_id: z.string(),
    zipcode: z.string(),
    has_pcp: z.boolean(),
    need_pcp_help: z.union([z.boolean(), z.null()]).optional(),
    default_pcp: z
      .union([infrastructure__schemas__user_schemas__PrimaryCareProviderSchema, z.null()])
      .optional(),
    custom_fields: z.array(GetUserCustomFieldInUserSchema),
    feature_flags: z.array(FeatureFlagSchema),
    hipaa_consent: z.union([HipaaConsentInfoSchema, z.null()]).optional(),
  })
  .loose()
const PrimaryCareProviderDTO = z
  .object({
    send_fax_results_to_pcp: z.boolean().default(false),
    pcp_name: z.union([z.string().min(1).max(90), z.null()]),
    pcp_fax: z.union([z.string().min(1).max(16).regex(/^\d{10}$/), z.null()]),
    pcp_address1: z.union([z.string().min(1).max(64), z.null()]),
    pcp_address2: z.union([z.string().max(64), z.null()]),
    pcp_city: z.union([z.string().min(1).max(45), z.null()]),
    pcp_state: z.union([z.string().min(2).max(2), z.null()]),
    pcp_zip: z.union([z.string().min(1).max(10), z.null()]),
  })
  .partial()
  .loose()
const UpdateUserDTO = z
  .object({
    first_name: z.union([z.string().max(45), z.null()]),
    last_name: z.union([z.string().max(45), z.null()]),
    preferred_name: z.union([z.string().max(45), z.null()]),
    address1: z.union([z.string().max(64), z.null()]),
    address2: z.union([z.string().max(64), z.null()]),
    city: z.union([z.string().max(45), z.null()]),
    zipcode: z.union([z.string().max(10), z.null()]),
    state: z.union([z.string().min(2).max(2), z.null()]),
    country: z.union([z.string().max(16), z.null()]),
    dob: z.union([z.string(), z.null()]),
    sex_assigned_at_birth: z.union([z.string().max(8), z.null()]),
    gender_identity: z.union([z.string().max(128), z.null()]),
    phone: z.union([z.string().max(16), z.null()]),
    custom_fields: z.record(z.string(), z.union([z.string(), z.null()])),
    has_pcp: z.union([z.boolean(), z.null()]),
    need_pcp_help: z.union([z.boolean(), z.null()]),
    default_pcp_id: z.union([z.string(), z.null()]),
    default_pcp: z.union([PrimaryCareProviderDTO, z.null()]),
  })
  .partial()
  .loose()
const PatientUserModelSchema = z
  .object({
    address1: z.string(),
    address2: z.union([z.string(), z.null()]).optional(),
    city: z.string(),
    country: z.string(),
    date_user_joined: z.iso.datetime({ local: true }),
    dob: z.string(),
    email: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    gender_identity: z.union([z.string(), z.null()]).optional(),
    partner_id: z.string(),
    phone: z.string(),
    preferred_name: z.union([z.string(), z.null()]).optional(),
    sex_assigned_at_birth: z.string(),
    state: z.string(),
    user_id: z.string(),
    zipcode: z.string(),
    has_pcp: z.boolean(),
    need_pcp_help: z.union([z.boolean(), z.null()]).optional(),
    default_pcp: z
      .union([infrastructure__schemas__user_schemas__PrimaryCareProviderSchema, z.null()])
      .optional(),
    custom_fields: z.array(GetUserCustomFieldInUserSchema),
  })
  .loose()
const PrimaryCareProviderEntity = z
  .object({
    id: z.string(),
    user_id: z.union([z.string(), z.null()]),
    send_fax_results_to_pcp: z.union([z.boolean(), z.null()]),
    pcp_name: z.union([z.string(), z.null()]),
    pcp_fax: z.union([z.string(), z.null()]),
    pcp_address1: z.union([z.string(), z.null()]).optional(),
    pcp_address2: z.union([z.string(), z.null()]).optional(),
    pcp_city: z.union([z.string(), z.null()]).optional(),
    pcp_state: z.union([z.string(), z.null()]).optional(),
    pcp_zip: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const KitOrderTestsWithProviderSchema = z
  .object({
    kitId: z.string(),
    partnerId: z.string(),
    labName: z.union([z.string(), z.null()]).optional(),
    labInfo: z.union([KitOrderLabInfoSchema, z.null()]).optional(),
    pdf: z.union([z.string(), z.null()]).optional(),
    pdfEntities: z.union([z.array(z.object({}).partial().loose()), z.null()]).optional(),
    pdfs: z.union([z.array(z.union([z.string(), z.null()])), z.null()]).optional(),
    results: z.array(KitOrderResultLineWithCorrectionSchema),
    dateReported: z.union([z.string(), z.null()]).optional(),
    providerInfo: ProviderInfoOutput,
  })
  .loose()
const KitHistoricalEventDTO = z
  .object({ event: z.string(), timestamp: z.iso.datetime({ local: true }) })
  .loose()
const KitOrderRejectionSchema = z
  .object({
    test_name: z.string(),
    reason: z.string(),
    is_recoverable: z.union([z.boolean(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const KitWithResultsSchema = z
  .object({
    kit_id: z.string(),
    user_id: z.number().int(),
    kit_registration_date: z.iso.datetime({ local: true }),
    has_pcp: z.boolean(),
    pcp: z.union([PrimaryCareProviderEntity, z.null()]).optional(),
    instruction_id: z.union([z.string(), z.null()]),
    url: z.string(),
    tests: z.union([KitOrderTestsWithProviderSchema, z.null()]).optional(),
    status: z.union([z.string(), z.null()]).optional(),
    sku: z.union([z.string(), z.null()]).optional(),
    pretty_sku_name: z.union([z.string(), z.null()]).optional(),
    events: z.array(KitHistoricalEventDTO),
    codes: z.array(KitOrderCodeSchema),
    rejection_reasons: z.array(KitOrderRejectionSchema).optional(),
    inbound_tracking_id: z.union([z.string(), z.null()]).optional(),
    outbound_tracking_id: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const PaginatedResponseSchema_KitWithResultsSchema_ = z
  .object({
    items: z.array(KitWithResultsSchema),
    page_size: z.number().int(),
    page: z.number().int().optional().default(0),
    total_pages: z.number().int().optional().default(0),
    total_items: z.number().int().optional().default(0),
  })
  .loose()
const PatientConsentRequestDTO = z
  .object({
    consented_at: z.union([z.string(), z.null()]),
    agreement_id: z.union([z.number(), z.null()]),
    version_id: z.union([z.number(), z.null()]),
  })
  .partial()
  .loose()
const RegisterKitDTO = z
  .object({
    kit_id: z.string(),
    has_pcp: z.boolean().optional().default(false),
    pcp_id: z.union([z.string(), z.null()]).optional(),
    pcp: z.union([PrimaryCareProviderDTO, z.null()]).optional(),
    patient_consent: z.union([PatientConsentRequestDTO, z.null()]).optional(),
  })
  .loose()
const UserKitSchema = z
  .object({
    kit_id: z.string(),
    user_id: z.number().int(),
    kit_registration_date: z.iso.datetime({ local: true }),
    has_pcp: z.boolean(),
    pcp: z.union([PrimaryCareProviderEntity, z.null()]).optional(),
  })
  .loose()
const HTTPCustomExceptionSchema = z
  .object({ status_code: z.number().int(), detail: z.string(), custom_code: z.string() })
  .loose()
const KitTrackingUrlsSchema = z
  .object({
    inbound_tracking_url: z.union([z.string(), z.null()]),
    outbound_tracking_url: z.union([z.string(), z.null()]),
  })
  .partial()
  .loose()
const CreateHipaaConsentDTO = z
  .object({ version_id: z.union([z.number(), z.null()]) })
  .partial()
  .loose()
const HipaaConsentSchema = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    consented_at: z.iso.datetime({ local: true }),
    expires_at: z.iso.datetime({ local: true }),
    version_id: z.union([z.number(), z.null()]).optional(),
  })
  .loose()
const ValidateKitDTO = z.object({ kit_id: z.string() }).loose()
const SexAtBirth = z.enum(['male', 'female'])
const PatientPrimaryCareVerificationDTO = z
  .object({ firstName: z.string(), lastName: z.string(), dob: z.string(), sexAtBirth: SexAtBirth })
  .loose()
const OrderAutofillPCPAddressSchema = z
  .object({
    address1: z.string(),
    address2: z.union([z.string(), z.null()]).optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  })
  .loose()
const OrderAutofillPatientAddressSchema = z
  .object({
    address1: z.union([z.string(), z.null()]),
    address2: z.union([z.string(), z.null()]),
    city: z.union([z.string(), z.null()]),
    state: z.union([z.string(), z.null()]),
    zip: z.union([z.string(), z.null()]),
    country: z.union([z.string(), z.null()]),
  })
  .partial()
  .loose()
const OrderAutofillResponseSchema = z
  .object({
    name: z.union([z.string(), z.null()]),
    npi: z.union([z.string(), z.null()]),
    address: z.union([OrderAutofillPCPAddressSchema, z.null()]),
    fax: z.union([z.string(), z.null()]),
    patient_address: z.union([OrderAutofillPatientAddressSchema, z.null()]),
  })
  .partial()
  .loose()
const CreateEligibilityQuestionnaireDTO = z
  .object({
    kitId: z.string().max(10),
    responses: z.array(EligibilityQuestionnaireItemDTO).min(1).max(50),
  })
  .loose()
const KitPCPSchema = z
  .object({
    kit_id: z.string(),
    pcp_name: z.string(),
    pcp_fax: z.union([z.string(), z.null()]).optional(),
    pcp_address1: z.union([z.string(), z.null()]).optional(),
    pcp_address2: z.union([z.string(), z.null()]).optional(),
    pcp_city: z.union([z.string(), z.null()]).optional(),
    pcp_state: z.union([z.string(), z.null()]).optional(),
    pcp_zip: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const KitAddressUpdateLookupResponse = z
  .object({ is_already_corrected: z.boolean(), kit_id: z.union([z.string(), z.null()]).optional() })
  .loose()
const AddressUpdateSubmitDTO = z
  .object({
    address1: z.string().min(1).max(64),
    address2: z.union([z.string().max(64), z.null()]).optional(),
    city: z.string().min(1).max(45),
    state: z.string().min(2).max(2),
    zip: z.string().min(1).max(10),
  })
  .loose()
const KitAddressUpdateSubmitResponse = z.object({ replacement_kit_id: z.string() }).loose()
const ConfirmKitAddressIdentityDTO = z
  .object({
    first_name: z.string().min(1).max(45),
    last_name: z.string().min(1).max(45),
    date_of_birth: z.string(),
  })
  .loose()
const KitAddressUpdatePreviousAddressResponse = z
  .object({
    address1: z.string(),
    address2: z.union([z.string(), z.null()]),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  })
  .loose()
const KitAddressUpdateIdentityResponse = z
  .object({
    kit_id: z.string(),
    sku_display_name: z.union([z.string(), z.null()]),
    previous_address: KitAddressUpdatePreviousAddressResponse,
    confirmation_token: z.string(),
  })
  .loose()
const AddressAutocompleteRequestDTO = z
  .object({
    query: z.string().min(1),
    country: z.string().min(3).max(3).optional().default('USA'),
    latitude: z.union([z.number(), z.null()]).optional(),
    longitude: z.union([z.number(), z.null()]).optional(),
    max_results: z.number().int().gte(1).lte(10).optional().default(10),
  })
  .loose()
const AddressComponentSchema = z
  .object({
    line1: z.string(),
    line2: z.union([z.string(), z.null()]).optional(),
    city: z.string(),
    state: z.string(),
    postal_code: z.string(),
    country: z.string(),
  })
  .loose()
const AddressSuggestionSchema = z
  .object({ place_id: z.string(), label: z.string(), address: AddressComponentSchema })
  .loose()
const AddressAutocompleteResponseSchema = z
  .object({ suggestions: z.array(AddressSuggestionSchema) })
  .loose()
const PatientSignUpStytchDTO = z
  .object({
    login: z.string().min(1).max(64),
    partner_id: z.string().min(1).max(45),
    dob: z.string(),
    sex_at_birth: z.string().min(1).max(8),
    first_name: z.string().min(1).max(45),
    last_name: z.string().min(1).max(45),
    preferred_name: z.union([z.string().max(45), z.null()]).optional(),
    address1: z.string().min(1).max(64),
    address2: z.union([z.string().max(64), z.null()]).optional(),
    city: z.string().min(1).max(45),
    state: z.string().min(2).max(2),
    country: z.string().min(1).max(16).optional().default('US'),
    zipcode: z.string().min(1).max(10),
    gender_identity: z.string().max(128),
    phone: z.string().min(1).max(16),
    custom_fields: z.record(z.string(), z.union([z.string(), z.null()])).optional(),
    domain: z.union([z.string(), z.null()]).optional(),
    partner_ids: z.array(z.string()).optional(),
    has_pcp: z.boolean().optional().default(false),
    need_pcp_help: z.union([z.boolean(), z.null()]).optional(),
    default_pcp: z.union([PrimaryCareProviderDTO, z.null()]).optional(),
  })
  .loose()
const GetCustomFieldInUserEntity = z.object({ field_name: z.string() }).loose()
const GetUserCustomFieldInUserEntity = z
  .object({ value: z.union([z.string(), z.null()]), field: GetCustomFieldInUserEntity })
  .loose()
const infrastructure__schemas__common__PrimaryCareProviderSchema = z
  .object({
    id: z.string(),
    user_id: z.union([z.string(), z.null()]).optional(),
    send_fax_results_to_pcp: z.boolean().optional().default(false),
    pcp_name: z.union([z.string(), z.null()]).optional(),
    pcp_fax: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const PatientUserExtendedSchema = z
  .object({
    address1: z.string(),
    address2: z.union([z.string(), z.null()]).optional(),
    city: z.string(),
    country: z.string(),
    created: z.boolean(),
    custom_fields: z.array(GetUserCustomFieldInUserEntity),
    date_user_joined: z.iso.datetime({ local: true }),
    dob: z.string(),
    email: z.string(),
    first_name: z.string(),
    gender_identity: z.union([z.string(), z.null()]).optional(),
    last_name: z.string(),
    partner_id: z.string(),
    phone: z.string(),
    preferred_name: z.union([z.string(), z.null()]).optional(),
    sex_assigned_at_birth: z.string(),
    state: z.string(),
    user_id: z.string(),
    zipcode: z.string(),
    has_pcp: z.boolean(),
    need_pcp_help: z.union([z.boolean(), z.null()]).optional(),
    default_pcp: z
      .union([infrastructure__schemas__common__PrimaryCareProviderSchema, z.null()])
      .optional(),
  })
  .loose()
const RegisterPatientUserWithKitAndHipaaConsentDTO = z
  .object({
    user: PatientSignUpStytchDTO,
    kit: RegisterKitDTO,
    hipaa_consent: CreateHipaaConsentDTO,
  })
  .loose()
const RegisterPatientUserWithKitAndHipaaConsentResponseSchema = z
  .object({
    user: PatientUserExtendedSchema,
    kit: UserKitSchema,
    hipaa_consent: z.union([HipaaConsentSchema, z.null()]),
  })
  .loose()
const Role = z.enum([
  'client-portal.super-administrator',
  'client-portal.organization-admin',
  'client-portal.administrator',
  'client-portal.non-phi-organization-admin',
  'client-portal.non-phi-program-admin',
  'patient-portal-user',
])
const ClientInviteDTO = z
  .object({
    partner_id: z.union([z.string(), z.null()]).optional(),
    partner_ids: z.array(z.string()).optional(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
    invited_by: z.string(),
    role: Role,
  })
  .loose()
const ClientUserWithUserIdSchema = z
  .object({
    first_name: z.union([z.string(), z.null()]).optional(),
    last_name: z.union([z.string(), z.null()]).optional(),
    email: z.string(),
    invited_by: z.union([z.string(), z.null()]).optional(),
    invited_date: z.iso.datetime({ local: true }),
    is_active: z.boolean(),
    user_id: z.union([z.number(), z.null()]).optional(),
    deleted_at: z.union([z.string(), z.null()]).optional(),
    deleted_by: z.union([z.number(), z.null()]).optional(),
    client_id: z.string().optional().default('client-portal'),
  })
  .loose()
const ClientUserAfterInviteSchema = z
  .object({ user: ClientUserWithUserIdSchema, created: z.boolean() })
  .loose()
const ClientSignUpInStytchFlowDTO = z.object({ email: z.string() }).loose()
const AnalyticsDimensionCapability = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  kind: z.string(),
  grains: z.array(z.string()),
})
const AnalyticsMetricCapability = z.object({
  id: z.string(),
  description: z.string(),
  unit: z.string(),
  allowedRenderers: z.array(
    z.enum([
      'kpi',
      'breakdown-bar',
      'pie',
      'donut',
      'threshold-time-series',
      'combination',
      'table',
    ]),
  ),
})
const AnalyticsRelationCapability = z.object({
  id: z.string(),
  description: z.string(),
  dimensions: z.array(AnalyticsDimensionCapability),
  metrics: z.array(AnalyticsMetricCapability),
})
const AnalyticsCapabilities = z.object({
  catalogVersion: z.number().int(),
  relations: z.array(AnalyticsRelationCapability),
  supportedRenderers: z.array(
    z.enum([
      'kpi',
      'breakdown-bar',
      'pie',
      'donut',
      'threshold-time-series',
      'combination',
      'table',
    ]),
  ),
  suggestedQuestions: z.array(z.string()),
})
const AnswerAnalyticsQuestionRequestDTO = z
  .object({
    partner_id: z.union([z.string(), z.null()]).optional(),
    partner_ids: z.array(z.string()).min(1).max(20).optional(),
    question: z.string().min(1).max(500),
    start_date: z.union([z.string(), z.null()]).optional(),
    end_date: z.union([z.string(), z.null()]).optional(),
  })
  .loose()
const AnalyticsChartMetadata = z.object({
  title: z.string(),
  subtitle: z.union([z.string(), z.null()]).optional(),
  ariaLabel: z.string(),
  exportFilename: z.union([z.string(), z.null()]).optional(),
})
const AnalyticsTimeframe = z.object({
  label: z.string(),
  startDate: z.union([z.string(), z.null()]).optional(),
  endDate: z.union([z.string(), z.null()]).optional(),
  timezone: z.string(),
  dateOnlyBuckets: z.boolean().optional().default(true),
})
const KpiVisualization = z.object({
  type: z.string().optional().default('kpi'),
  value: z.number(),
  format: z.enum(['integer', 'decimal', 'percent', 'duration-hours', 'duration-days']),
  numerator: z.union([z.number(), z.null()]).optional(),
  denominator: z.union([z.number(), z.null()]).optional(),
})
const BreakdownBarValue = z.object({
  categoryKey: z.string(),
  categoryLabel: z.string(),
  value: z.union([z.number(), z.null()]),
})
const BreakdownBarCategory = z.object({
  categoryKey: z.string(),
  categoryLabel: z.string(),
  values: z.array(BreakdownBarValue),
})
const BreakdownBarVisualization = z.object({
  type: z.string().optional().default('breakdown-bar'),
  unit: z.enum(['count', 'percent', 'hours', 'days']),
  categories: z.array(BreakdownBarCategory),
})
const PieSegment = z.object({
  categoryKey: z.string(),
  categoryLabel: z.string(),
  value: z.number(),
})
const PieVisualization = z.object({
  type: z.string().optional().default('pie'),
  unit: z.enum(['count', 'percent']),
  segments: z.array(PieSegment),
})
const DonutSegment = z.object({
  categoryKey: z.string(),
  categoryLabel: z.string(),
  value: z.number(),
})
const DonutVisualization = z.object({
  type: z.string().optional().default('donut'),
  unit: z.enum(['count', 'percent']),
  segments: z.array(DonutSegment),
})
const TimeSeriesPoint = z.object({
  date: z.string(),
  value: z.union([z.number(), z.null()]),
  numerator: z.union([z.number(), z.null()]).optional(),
  denominator: z.union([z.number(), z.null()]).optional(),
})
const TimeSeriesSeries = z.object({
  categoryKey: z.string(),
  categoryLabel: z.string(),
  points: z.array(TimeSeriesPoint),
})
const Threshold = z.object({ label: z.string(), value: z.number() })
const ThresholdTimeSeriesVisualization = z.object({
  type: z.string().optional().default('threshold-time-series'),
  unit: z.enum(['count', 'percent', 'hours', 'days']),
  series: z.array(TimeSeriesSeries),
  thresholds: z.array(Threshold).optional().default([]),
})
const AnalyticsCategory = z.object({ categoryKey: z.string(), categoryLabel: z.string() })
const CombinationValue = z.object({ bucketKey: z.string(), value: z.union([z.number(), z.null()]) })
const CombinationSeries = z.object({
  categoryKey: z.string(),
  categoryLabel: z.string(),
  chartType: z.enum(['column', 'line']),
  unit: z.enum(['count', 'percent', 'hours', 'days']),
  values: z.array(CombinationValue),
})
const CombinationVisualization = z.object({
  type: z.string().optional().default('combination'),
  buckets: z.array(AnalyticsCategory),
  series: z.array(CombinationSeries),
})
const TableVisualization = z.object({ type: z.string().default('table') }).partial()
const AnalyticsTableColumn = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['text', 'number', 'date', 'percent', 'duration']),
  sortable: z.boolean().optional().default(true),
})
const AnalyticsTableRow = z.object({
  id: z.string(),
  cells: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
})
const AnalyticsTableModel = z.object({
  columns: z.array(AnalyticsTableColumn),
  rows: z.array(AnalyticsTableRow),
  emptyMessage: z.string(),
  pageSize: z.union([z.number(), z.null()]).optional(),
})
const AnalyticsResult = z.object({
  version: z.number().int().optional().default(1),
  question: z.string(),
  answer: z.union([z.string(), z.null()]).optional(),
  requestedRenderer: z
    .union([
      z.enum([
        'kpi',
        'breakdown-bar',
        'pie',
        'donut',
        'threshold-time-series',
        'combination',
        'table',
      ]),
      z.null(),
    ])
    .optional(),
  selectedRenderer: z.enum([
    'kpi',
    'breakdown-bar',
    'pie',
    'donut',
    'threshold-time-series',
    'combination',
    'table',
  ]),
  rendererIntent: z.enum(['explicit', 'preferred', 'inferred']),
  rendererFallbackReason: z.union([z.string(), z.null()]).optional(),
  chartMetadata: AnalyticsChartMetadata,
  timeframe: AnalyticsTimeframe,
  methodologyNotes: z.array(z.string()),
  visualization: z
    .union([
      z.discriminatedUnion('type', [
        KpiVisualization,
        BreakdownBarVisualization,
        PieVisualization,
        DonutVisualization,
        ThresholdTimeSeriesVisualization,
        CombinationVisualization,
        TableVisualization,
      ]),
      z.null(),
    ])
    .optional(),
  table: AnalyticsTableModel,
})
const PinnedChartResponseSchema = z
  .object({
    id: z.string(),
    question: z.string(),
    title: z.string(),
    partner_ids: z.array(z.string()),
    start_date: z.union([z.string(), z.null()]),
    end_date: z.union([z.string(), z.null()]),
    result: AnalyticsResult,
    created_by: z.number().int(),
    created_at: z.iso.datetime({ local: true }),
  })
  .loose()
const CreatePinnedChartRequestDTO = z
  .object({
    partner_id: z.union([z.string(), z.null()]).optional(),
    partner_ids: z.array(z.string()).min(1).max(20).optional(),
    result: AnalyticsResult,
  })
  .loose()
const PinnedChartCapacityErrorSchema = z
  .object({ detail: z.string(), portal_error_code: z.string() })
  .loose()
const QuestionHistoryEntryResponseSchema = z
  .object({
    id: z.string().uuid(),
    question: z.string(),
    asked_at: z.iso.datetime({ local: true }),
  })
  .loose()
const RecordQuestionHistoryRequestDTO = z.object({ question: z.string().min(1).max(500) })
const QuestionOptionDTO = z.object({ value: z.string(), label: z.string() })
const ConditionValueMatcherDTO = z
  .object({
    equals: z.union([z.string(), z.boolean(), z.number(), z.number(), z.null()]),
    anyOf: z.union([
      z.array(z.union([z.string(), z.boolean(), z.number(), z.number(), z.null()])),
      z.null(),
    ]),
  })
  .partial()
const QuestionnaireConditionLeafDTO = z.object({
  questionId: z.union([z.string(), z.null()]).optional(),
  configId: z.union([z.string(), z.null()]).optional(),
  valueMatcher: ConditionValueMatcherDTO,
})
const QuestionnaireConditionInnerGroupDTO = z.object({
  operator: z.enum(['and', 'or']),
  conditions: z.array(QuestionnaireConditionLeafDTO).min(1),
})
const QuestionnaireConditionMiddleGroupDTO = z.object({
  operator: z.enum(['and', 'or']),
  conditions: z
    .array(z.union([QuestionnaireConditionLeafDTO, QuestionnaireConditionInnerGroupDTO]))
    .min(1),
})
const QuestionnaireConditionOuterGroupDTO = z.object({
  operator: z.enum(['and', 'or']),
  conditions: z
    .array(z.union([QuestionnaireConditionLeafDTO, QuestionnaireConditionMiddleGroupDTO]))
    .min(1),
})
const QuestionnaireConditionDTO = z.union([
  QuestionnaireConditionLeafDTO,
  QuestionnaireConditionOuterGroupDTO,
])
const QuestionDefaultDTO = z.object({
  if: z.array(QuestionnaireConditionDTO),
  then: z.union([z.string(), z.boolean(), z.number(), z.number(), z.null()]),
  else: z.union([z.string(), z.boolean(), z.number(), z.number(), z.null()]),
})
const QuestionDefaultValue = z.union([
  QuestionDefaultDTO,
  z.string(),
  z.boolean(),
  z.number(),
  z.number(),
  z.null(),
])
const QuestionDefinitionDTO = z.object({
  questionId: z.string(),
  label: z.string(),
  type: z.enum([
    'text',
    'number',
    'tel',
    'date',
    'datetime',
    'radio',
    'boolean',
    'select',
    'multi_select',
    'content',
    'address',
  ]),
  required: z.boolean().optional().default(false),
  options: z.union([z.array(QuestionOptionDTO), z.null()]).optional(),
  displayIf: z.array(QuestionnaireConditionDTO).optional(),
  default: QuestionDefaultValue.optional(),
  requiredIf: z.array(QuestionnaireConditionDTO).optional(),
  validationRules: z.union([z.object({}).partial().loose(), z.null()]).optional(),
  variant: z
    .union([
      z.enum(['stacked', 'inline', 'inline_spaced']),
      z.enum(['error', 'confirm_eligible']),
      z.null(),
    ])
    .optional(),
  description: z.union([z.string(), z.null()]).optional(),
})
const QuestionnairePageDTO = z.object({
  pageName: z.string(),
  position: z.string(),
  questions: z.array(QuestionDefinitionDTO),
  displayIf: z.array(QuestionnaireConditionDTO).optional(),
  allowContinue: z.boolean().optional().default(true),
})
const EligibilityQuestionnaireDTO = z.object({
  version: z.number().int().optional().default(1),
  pages: z.array(QuestionnairePageDTO),
  skuList: z.union([z.array(z.string()), z.null()]).optional(),
})
const EditEligibilityQuestionnaireRequestDTO = z
  .object({
    partner_id: z.union([z.string(), z.null()]).optional(),
    partner_ids: z.array(z.string()).optional(),
    prompt: z.string().min(1),
    session_id: z.string().min(1),
    current_questionnaire: EligibilityQuestionnaireDTO,
  })
  .loose()
const EditEligibilityQuestionnaireResponseDTO = z
  .object({ questionnaire: EligibilityQuestionnaireDTO, summary: z.string().min(1) })
  .loose()
const OrderStatusCountSchema = z
  .object({ status: z.string(), count: z.number().int() })
  .loose()
const AggregatedDataSchema = z
  .object({ date: z.string(), data: z.array(OrderStatusCountSchema) })
  .loose()
const GetAggregatedDataSchema = z
  .object({ aggregated_data: z.array(AggregatedDataSchema) })
  .loose()
const OrdersByStateItemSchema = z
  .object({ state: z.string(), count: z.number().int() })
  .loose()
const GetOrdersByStateSchema = z.object({ results: z.array(OrdersByStateItemSchema) }).loose()
const days_threshold_turnaround = z.union([z.number(), z.null()]).optional()
const percentile = z.union([z.number(), z.string(), z.null()]).optional()
const TurnaroundTimeSchema = z
  .object({
    kit_count: z.union([z.number(), z.null()]),
    pct_kits_days_or_less: z.union([z.number(), z.null()]).optional(),
    kits_over_days_threshold: z.array(z.string()).optional().default([]),
    avg_turnaround_business_days: z.union([z.number(), z.null()]),
    percentile_turnaround_business_days: z.union([z.number(), z.null()]),
  })
  .loose()
const LabProcessingTimeSchema = z
  .object({
    kit_count: z.union([z.number(), z.null()]),
    pct_kits_days_or_less: z.union([z.number(), z.null()]).optional(),
    kits_over_days_threshold: z.array(z.string()).optional().default([]),
    avg_lab_processing_business_days: z.union([z.number(), z.null()]),
    percentile_lab_processing_business_days: z.union([z.number(), z.null()]),
  })
  .loose()
const TimeMetricsSchema = z
  .object({
    turnaround_time: z.union([TurnaroundTimeSchema, z.null()]),
    lab_processing_time: z.union([LabProcessingTimeSchema, z.null()]),
  })
  .loose()
const MostRecentOrderSortByInfoDTO = z
  .object({ sortByEvent: z.string(), sortByEventTimestamp: z.iso.datetime({ local: true }) })
  .loose()
const MostRecentOrderShippingDTO = z
  .object({
    firstName: z.union([z.string(), z.null()]),
    lastName: z.union([z.string(), z.null()]),
    email: z.union([z.string(), z.null()]),
    phone: z.union([z.string(), z.null()]),
    state: z.union([z.string(), z.null()]),
  })
  .partial()
  .loose()
const MostRecentOrderFulfillmentDTO = z
  .object({
    outboundTrackingId: z.union([z.string(), z.null()]),
    inboundTrackingId: z.union([z.string(), z.null()]),
    outboundShippingMethod: z.union([z.string(), z.null()]),
    inboundShippingMethod: z.union([z.string(), z.null()]),
  })
  .partial()
  .loose()
const MostRecentOrderPatientInfoDTO = z
  .object({ firstName: z.union([z.string(), z.null()]), lastName: z.union([z.string(), z.null()]) })
  .partial()
  .loose()
const OrderSchema = z
  .object({
    kitId: z.string(),
    orderId: z.string(),
    partnerId: z.string(),
    orderType: z.array(z.string()).optional(),
    currentStatus: z.union([z.string(), z.null()]).optional(),
    completed: z.union([z.boolean(), z.null()]).optional(),
    automaticallyRegistered: z.boolean().optional().default(false),
    orderReceivedTimestamp: z.union([z.string(), z.null()]).optional(),
    sortByInfo: z.union([MostRecentOrderSortByInfoDTO, z.null()]).optional(),
    sku: z.union([z.string(), z.null()]).optional(),
    skuName: z.union([z.string(), z.null()]).optional(),
    shipping: z.union([MostRecentOrderShippingDTO, z.null()]).optional(),
    fulfillment: z.union([MostRecentOrderFulfillmentDTO, z.null()]).optional(),
    patientInfo: z.union([MostRecentOrderPatientInfoDTO, z.null()]).optional(),
    additionalFields: z.union([z.object({}).partial().loose(), z.null()]).optional(),
    kit_fulfillment_cancellation_failed: z.boolean().optional().default(false),
    kit_fulfillment_cancellation_failed_timestamp: z.union([z.string(), z.null()]).optional(),
    shownExtraFields: z.union([z.object({}).partial().loose(), z.null()]).optional(),
    hiddenFields: z.union([z.array(z.string()), z.null()]).optional(),
    isAbnormal: z.boolean().optional().default(false),
    hasCorrectedResults: z.boolean().optional().default(false),
  })
  .loose()
const OrderPaginatedSchema = z
  .object({
    currentPage: z.number().int().optional().default(0),
    pageSize: z.number().int().optional().default(0),
    totalPages: z.number().int().optional().default(0),
    totalItems: z.number().int().optional().default(0),
    orders: z.array(OrderSchema),
    salesDemoEnabled: z.boolean().optional().default(false),
  })
  .loose()
const RecentOrdersSearchPairDTO = z
  .object({ search_field: z.array(z.string()).min(1), search_keyword: z.string().min(1).max(64) })
  .loose()
const RecentOrdersSearchRequestDTO = z
  .object({
    partner_id: z.union([z.string(), z.null()]),
    partner_ids: z.array(z.string()),
    sort_by_field: z.union([z.array(z.string()), z.null()]),
    search_field: z.union([z.array(z.string()), z.null()]),
    search_keyword: z.union([z.string().min(1).max(64), z.null()]),
    searches: z.union([z.array(RecentOrdersSearchPairDTO), z.null()]),
    current_status: z.union([z.array(z.string()), z.null()]),
    has_abnormal_results: z.union([z.boolean(), z.null()]),
    automatically_registered: z.union([z.boolean(), z.null()]),
    rejection_reason: z.union([z.array(z.string()), z.null()]),
    completed: z.union([z.boolean(), z.null()]),
    page: z.number().int().gte(1).default(1),
    page_size: z.number().int().gte(1).lte(100).default(25),
  })
  .partial()
  .loose()
const CorrectionBatchDTO = z
  .object({ desiredResults: z.array(DesiredTestResultDTO) })
  .partial()
  .loose()
const RequestMultipleTestOrderCorrectionsDTO = z
  .object({
    status: OrderAPIOrderEvents.optional(),
    corrections: z.array(CorrectionBatchDTO).min(1).max(5),
  })
  .loose()
const CreatedTestOrderCorrectionSchema = z
  .object({ resultCodeId: z.number().int(), value: z.string(), correctionNumber: z.number().int() })
  .loose()
const RequestMultipleTestOrderCorrectionsSchema = z
  .object({
    orderId: z.string(),
    kitId: z.string(),
    partnerId: z.string(),
    corrections: z.array(CreatedTestOrderCorrectionSchema),
  })
  .loose()
const SetCompletionStatusDTO = z
  .object({ kit_ids: z.array(z.string()).max(500), completed: z.boolean() })
  .loose()
const BatchClientOrderPCPInfoRequestDTO = z
  .object({ kit_ids: z.array(z.string()).min(1).max(500), partner_id: z.string().min(1) })
  .loose()
const PortalBatchPCPInfoSchema = z
  .object({
    kit_id: z.string(),
    need_pcp_help: z.union([z.boolean(), z.null()]).optional(),
    patient_provided_pcp: z.union([PatientProvidedPCPSchema, z.null()]).optional(),
  })
  .loose()
const BatchPCPInfoErrorReason = z.enum([
  'not_found',
  'not_partner_owned',
  'invalid_kit_id',
  'upstream_error',
])
const BatchPCPInfoErrorSchema = z
  .object({ kit_id: z.string(), reason: BatchPCPInfoErrorReason })
  .loose()
const BatchPCPInfoMetaSchema = z
  .object({
    requested_count: z.number().int(),
    results_count: z.number().int(),
    errors_count: z.number().int(),
  })
  .loose()
const BatchClientOrderPCPInfoResponseSchema = z
  .object({
    results: z.array(PortalBatchPCPInfoSchema),
    errors: z.array(BatchPCPInfoErrorSchema),
    meta: BatchPCPInfoMetaSchema,
  })
  .loose()
const QuestionnaireDTO = z.object({
  version: z.number().int().optional().default(1),
  pages: z.array(QuestionnairePageDTO),
})
const ProgramAnalyticsMetric = z.enum(['return_rate', 'result_rate', 'total_orders'])
const ProgramAnalyticsRequestDTO = z
  .object({
    partner_id: z.union([z.string(), z.null()]).optional(),
    partnerIds: z.array(z.string()).min(1).optional(),
    metrics: z.array(ProgramAnalyticsMetric).min(1),
  })
  .loose()
const ResultRateStatus = z.enum(['calculating', 'ready'])
const ProgramAnalyticsSummarySchema = z
  .object({
    returnRate: z.union([z.number(), z.null()]),
    resultRate: z.union([z.number(), z.null()]),
    resultRateStatus: z.union([ResultRateStatus, z.null()]),
    totalOrders: z.union([z.number(), z.null()]),
  })
  .partial()
  .loose()
const ProgramAnalyticsProgramSchema = z
  .object({
    partnerId: z.string(),
    returnRate: z.union([z.number(), z.null()]).optional(),
    resultRate: z.union([z.number(), z.null()]).optional(),
    resultRateStatus: z.union([ResultRateStatus, z.null()]).optional(),
    totalOrders: z.union([z.number(), z.null()]).optional(),
  })
  .loose()
const ProgramAnalyticsResponseSchema = z
  .object({
    summary: ProgramAnalyticsSummarySchema,
    programs: z.array(ProgramAnalyticsProgramSchema),
  })
  .loose()
const Body_ask_question_v3_tools_ai_agent_question_post = z
  .object({
    prompt: z.string(),
    session_id: z.string(),
    model: z.union([z.string(), z.null()]).optional(),
    files: z.union([z.array(z.instanceof(Blob)), z.null()]).optional(),
  })
  .loose()
const DevIdentityFromJwtRequestDTO = z
  .object({ jwt: z.string().min(1), auth_user_id: z.union([z.number(), z.null()]).optional() })
  .loose()
const UserType = z.enum(['application', 'user'])
const DevIdentityScopeDTO = z
  .object({ is_primary: z.boolean(), roles: z.array(z.string()) })
  .loose()
const DevIdentityFromJwtResponseDTO = z
  .object({
    primary_client_id: z.string(),
    organization_id: z.string().uuid(),
    id: z.number().int(),
    identity_type: UserType,
    is_active: z.boolean(),
    login: z.string(),
    scopes: z.record(z.string(), DevIdentityScopeDTO),
  })
  .loose()
const DashboardAggregationPeriods = z.enum(['daily', 'weekly', 'monthly', 'previous', 'current'])
const FileDisposition = z.enum(['inline', 'attachment'])
const PeriodName = z.enum(['week', 'month', 'quarter', 'year', 'custom'])
const RejectionTypeForAggregation = z.enum(['full', 'partial', 'all'])

export const schemas = {
  UpdateEmailDTO,
  partner_ids,
  partner_id,
  UserSchema,
  HTTPExceptionSchema,
  ValidationError,
  HTTPValidationError,
  UserDiscoveryDTO,
  UserDiscoverySchema,
  OrgClientSchema,
  OrgSchema,
  IdentityInfoSchema,
  FileAccessFlagValue,
  ClientFileStatus,
  ClientFileSchema,
  PartnerReportFileSchema,
  PartnerReportsSchema,
  ClientFilesResponseSchema,
  Body_upload_client_file_v2_clients_files_post,
  SignedFileUrlSchema,
  ClientFileQuarantinedErrorSchema,
  full_history,
  KitOrderLabInfoSchema,
  KitOrderResultLineSchema,
  KitOrderResultLineWithCorrectionSchema,
  ProviderInfoOutput,
  ResultsByKitIdSchema,
  get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
  OderDetailsSectionSchema,
  ShippingInfoSchema,
  TrackingInfoSchema,
  KitOrderResultCodeSchema,
  KitOrderCodeSchema,
  SingleEventSchema,
  OrderAdditionalFieldEntrySchema,
  ResultRejectionInfoSchema,
  AoeQuestionOptionSchema,
  AoeQuestionAnswerSchema,
  SampleCollectionTypeSchema,
  SampleCollectionConfigurationSchema,
  OrderDetailsWithShippingAndResultsSchema,
  PatientProvidedPCPSchema,
  OrderPrimaryCareProviderAddressSchema,
  OrderPrimaryCareProviderSchema,
  ClientOrderPCPInfoSchema,
  EligibilityQuestionnaireResponseSchema,
  EligibilityQuestionnaireResponsesSchema,
  OrderAPIOrderEvents,
  ShippingAddressDTO,
  PatientAddressDTO,
  PatientInfoDTO,
  ProviderInfoDTO,
  FulfillmentDTO,
  InsuranceDTO,
  DesiredTestResultDTO,
  SetTestOrderStatusDTO,
  SetTestOrderStatusSchema,
  AddKitEventDTO,
  AddKitEventSchema,
  RequestTestOrderCorrectionDTO,
  RequestTestOrderCorrectionSchema,
  KitValidationResponseSchema,
  ClientRegisterKitPatientInfoDTO,
  ClientRegisterKitPatientAddressDTO,
  ConsentAgreementType,
  ConsentAgreementSource,
  ClientRegisterKitConsentAgreementDTO,
  EligibilityQuestionnaireItemDTO,
  ClientRegisterKitPcpContactDTO,
  ClientRegisterKitPcpDTO,
  ClientRegisterKitDTO,
  KitRegistrationResponseSchema,
  CustomerIoMessageCustomerIdentifierEntity,
  CustomerIoMessageMetricsEntity,
  CustomerIoMessageSchema,
  OrderClientConfigurationSchema,
  ResultCodeType,
  ResultCodeWithReferenceRangesSchema,
  LabCodeSchema,
  SkuRejectionReasonSchema,
  SkuSchema,
  PaginatedResponseSchema_SkuSchema_,
  RejectionReasonSummarySchema,
  KitRejectionsSummarySchema,
  OrderAdditionalFieldSchema,
  ProviderSchema,
  CancelFulfillmentInputDTO,
  Npi,
  PrimaryCareProviderAddress,
  Fax,
  PrimaryCareProvider,
  PlaceOrderAoeAnswerDTO,
  LabBillingType,
  InsuranceAddress,
  InsuranceInfo,
  PlaceOrderDTO,
  partner_id__2,
  PlacedOrderSchema,
  InsuranceItem,
  InsuranceListResponse,
  ContinueKitInvestigationDTO,
  ClientUserProfileSchema,
  UpdateClientUserProfileDTO,
  FieldSorting,
  sort_by_name,
  ProgramDTO,
  ClientUserProfileListSchema,
  PaginatedResponseSchema_ClientUserProfileListSchema_,
  DeleteClientUserDTO,
  ClientInvitationReminderSchema,
  SendClientInvitationReminderDTO,
  ClientUserMfaStatusSchema,
  FeatureFlagSchema,
  ProgramStatus,
  PartnerSchema,
  ClientUserWithPartnersSchema,
  ImpersonateClientUserResponseSchema,
  FeatureFlagsResponseSchema,
  infrastructure__schemas__user_schemas__PrimaryCareProviderSchema,
  GetCustomFieldInUserSchema,
  GetUserCustomFieldInUserSchema,
  HipaaConsentInfoSchema,
  PatientUserModelExtendedSchema,
  PrimaryCareProviderDTO,
  UpdateUserDTO,
  PatientUserModelSchema,
  PrimaryCareProviderEntity,
  KitOrderTestsWithProviderSchema,
  KitHistoricalEventDTO,
  KitOrderRejectionSchema,
  KitWithResultsSchema,
  PaginatedResponseSchema_KitWithResultsSchema_,
  PatientConsentRequestDTO,
  RegisterKitDTO,
  UserKitSchema,
  HTTPCustomExceptionSchema,
  KitTrackingUrlsSchema,
  CreateHipaaConsentDTO,
  HipaaConsentSchema,
  ValidateKitDTO,
  SexAtBirth,
  PatientPrimaryCareVerificationDTO,
  OrderAutofillPCPAddressSchema,
  OrderAutofillPatientAddressSchema,
  OrderAutofillResponseSchema,
  CreateEligibilityQuestionnaireDTO,
  KitPCPSchema,
  KitAddressUpdateLookupResponse,
  AddressUpdateSubmitDTO,
  KitAddressUpdateSubmitResponse,
  ConfirmKitAddressIdentityDTO,
  KitAddressUpdatePreviousAddressResponse,
  KitAddressUpdateIdentityResponse,
  AddressAutocompleteRequestDTO,
  AddressComponentSchema,
  AddressSuggestionSchema,
  AddressAutocompleteResponseSchema,
  PatientSignUpStytchDTO,
  GetCustomFieldInUserEntity,
  GetUserCustomFieldInUserEntity,
  infrastructure__schemas__common__PrimaryCareProviderSchema,
  PatientUserExtendedSchema,
  RegisterPatientUserWithKitAndHipaaConsentDTO,
  RegisterPatientUserWithKitAndHipaaConsentResponseSchema,
  Role,
  ClientInviteDTO,
  ClientUserWithUserIdSchema,
  ClientUserAfterInviteSchema,
  ClientSignUpInStytchFlowDTO,
  AnalyticsDimensionCapability,
  AnalyticsMetricCapability,
  AnalyticsRelationCapability,
  AnalyticsCapabilities,
  AnswerAnalyticsQuestionRequestDTO,
  AnalyticsChartMetadata,
  AnalyticsTimeframe,
  KpiVisualization,
  BreakdownBarValue,
  BreakdownBarCategory,
  BreakdownBarVisualization,
  PieSegment,
  PieVisualization,
  DonutSegment,
  DonutVisualization,
  TimeSeriesPoint,
  TimeSeriesSeries,
  Threshold,
  ThresholdTimeSeriesVisualization,
  AnalyticsCategory,
  CombinationValue,
  CombinationSeries,
  CombinationVisualization,
  TableVisualization,
  AnalyticsTableColumn,
  AnalyticsTableRow,
  AnalyticsTableModel,
  AnalyticsResult,
  PinnedChartResponseSchema,
  CreatePinnedChartRequestDTO,
  PinnedChartCapacityErrorSchema,
  QuestionHistoryEntryResponseSchema,
  RecordQuestionHistoryRequestDTO,
  QuestionOptionDTO,
  ConditionValueMatcherDTO,
  QuestionnaireConditionLeafDTO,
  QuestionnaireConditionInnerGroupDTO,
  QuestionnaireConditionMiddleGroupDTO,
  QuestionnaireConditionOuterGroupDTO,
  QuestionnaireConditionDTO,
  QuestionDefaultDTO,
  QuestionDefaultValue,
  QuestionDefinitionDTO,
  QuestionnairePageDTO,
  EligibilityQuestionnaireDTO,
  EditEligibilityQuestionnaireRequestDTO,
  EditEligibilityQuestionnaireResponseDTO,
  OrderStatusCountSchema,
  AggregatedDataSchema,
  GetAggregatedDataSchema,
  OrdersByStateItemSchema,
  GetOrdersByStateSchema,
  days_threshold_turnaround,
  percentile,
  TurnaroundTimeSchema,
  LabProcessingTimeSchema,
  TimeMetricsSchema,
  MostRecentOrderSortByInfoDTO,
  MostRecentOrderShippingDTO,
  MostRecentOrderFulfillmentDTO,
  MostRecentOrderPatientInfoDTO,
  OrderSchema,
  OrderPaginatedSchema,
  RecentOrdersSearchPairDTO,
  RecentOrdersSearchRequestDTO,
  CorrectionBatchDTO,
  RequestMultipleTestOrderCorrectionsDTO,
  CreatedTestOrderCorrectionSchema,
  RequestMultipleTestOrderCorrectionsSchema,
  SetCompletionStatusDTO,
  BatchClientOrderPCPInfoRequestDTO,
  PortalBatchPCPInfoSchema,
  BatchPCPInfoErrorReason,
  BatchPCPInfoErrorSchema,
  BatchPCPInfoMetaSchema,
  BatchClientOrderPCPInfoResponseSchema,
  QuestionnaireDTO,
  ProgramAnalyticsMetric,
  ProgramAnalyticsRequestDTO,
  ResultRateStatus,
  ProgramAnalyticsSummarySchema,
  ProgramAnalyticsProgramSchema,
  ProgramAnalyticsResponseSchema,
  Body_ask_question_v3_tools_ai_agent_question_post,
  DevIdentityFromJwtRequestDTO,
  UserType,
  DevIdentityScopeDTO,
  DevIdentityFromJwtResponseDTO,
  DashboardAggregationPeriods,
  FileDisposition,
  PeriodName,
  RejectionTypeForAggregation,
}

const endpoints = makeApi([
  {
    method: 'post',
    path: '/dev/identity/from-jwt',
    alias: 'identity_from_jwt_dev_identity_from_jwt_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DevIdentityFromJwtRequestDTO,
      },
    ],
    response: DevIdentityFromJwtResponseDTO,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Origin not allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/healthy',
    alias: 'healthcheck_healthy_get',
    description: `Checks if the service is healthy.
A service is considered healthy if it can connect to the database and redis.`,
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/v2/auth/identity',
    alias: 'get_user_identity_v2_auth_identity_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: IdentityInfoSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/auth/organizations',
    alias: 'get_organizations_v2_auth_organizations_get',
    requestFormat: 'json',
    response: z.array(OrgSchema),
    errors: [
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/auth/organizations/:org_id/clients',
    alias: 'org_partners_v2_auth_organizations__org_id__clients_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'org_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.array(OrgClientSchema),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v2/auth/update-email',
    alias: 'update_email_v2_auth_update_email_patch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ email: z.string() }).loose(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: UserSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/auth/user-discovery',
    alias: 'discover_user_in_auth_admin_v2_auth_user_discovery_post',
    description: `Link the signed-in Stytch member to an existing auth user.

This route stays open at the auth gateway. A permission row blocks the first link,
because the member is not a PAS user yet. The handler reads the email and the
organization from the Stytch session cookie. The JSON body must match that session.
The handler does not replace an auth provider record id that is already set.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserDiscoveryDTO,
      },
      {
        name: 'x-auth-provider',
        type: 'Header',
        schema: z.string(),
      },
      {
        name: 'x-auth-provider-record-id',
        type: 'Header',
        schema: z.string(),
      },
    ],
    response: z.object({ is_discovered: z.boolean() }).loose(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/files',
    alias: 'list_client_files_v2_clients_files_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'search',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().gte(1).optional().default(1),
      },
      {
        name: 'page_size',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(25),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: ClientFilesResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/files',
    alias: 'upload_client_file_v2_clients_files_post',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Body_upload_client_file_v2_clients_files_post,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: ClientFileSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 502,
        description: `Bad Gateway`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v2/clients/files/:file_id',
    alias: 'delete_client_file_v2_clients_files__file_id__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'file_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/files/:file_id/url',
    alias: 'get_client_file_url_v2_clients_files__file_id__url_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'file_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'disposition',
        type: 'Query',
        schema: z.enum(['inline', 'attachment']).optional(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: SignedFileUrlSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 409,
        description: `Conflict`,
        schema: ClientFileQuarantinedErrorSchema,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/files/reports/url',
    alias: 'get_partner_report_file_url_v2_clients_files_reports_url_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'name',
        type: 'Query',
        schema: z.string().min(1).max(1024),
      },
      {
        name: 'disposition',
        type: 'Query',
        schema: z.enum(['inline', 'attachment']).optional(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: SignedFileUrlSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/orders/:kit_id/corrections',
    alias: 'request_test_order_correction_v2_clients_orders__kit_id__corrections_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RequestTestOrderCorrectionDTO,
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: RequestTestOrderCorrectionSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/:kit_id/details',
    alias: 'get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: OrderDetailsWithShippingAndResultsSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/:kit_id/eligibility-questionnaire',
    alias:
      'get_eligibility_questionnaire_responses_v2_clients_orders__kit_id__eligibility_questionnaire_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: EligibilityQuestionnaireResponsesSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/orders/:kit_id/events',
    alias: 'add_kit_event_v2_clients_orders__kit_id__events_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddKitEventDTO,
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: AddKitEventSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/orders/:kit_id/investigator/sessions',
    alias: 'start_kit_investigation_v2_clients_orders__kit_id__investigator_sessions_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 429,
        description: `Too Many Requests`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/orders/:kit_id/investigator/sessions/:session_id/messages',
    alias:
      'continue_kit_investigation_v2_clients_orders__kit_id__investigator_sessions__session_id__messages_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ prompt: z.string().min(1).max(4000) }).loose(),
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'session_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 429,
        description: `Too Many Requests`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/:kit_id/pcp-info',
    alias: 'get_client_order_pcp_info_v2_clients_orders__kit_id__pcp_info_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: ClientOrderPCPInfoSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/orders/:kit_id/registration',
    alias: 'register_client_kit_v2_clients_orders__kit_id__registration_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ClientRegisterKitDTO,
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: KitRegistrationResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 409,
        description: `Conflict`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/orders/:kit_id/statuses',
    alias: 'set_test_order_status_v2_clients_orders__kit_id__statuses_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetTestOrderStatusDTO,
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: SetTestOrderStatusSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/orders/:kit_id/validate',
    alias: 'validate_client_kit_v2_clients_orders__kit_id__validate_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: KitValidationResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 409,
        description: `Conflict`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/orders/:partner_id',
    alias: 'place_order_v2_clients_orders__partner_id__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlaceOrderDTO,
      },
      {
        name: 'partner_id',
        type: 'Path',
        schema: partner_id__2,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: PlacedOrderSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/additional-fields',
    alias: 'get_order_additional_fields_v2_clients_orders_additional_fields_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: z.array(OrderAdditionalFieldSchema),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/aggregated-data',
    alias: 'get_aggregated_data_v2_clients_orders_aggregated_data_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'end_date',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'period_in_days',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'aggregation_period',
        type: 'Query',
        schema: z.enum(['daily', 'weekly', 'monthly', 'previous', 'current']).optional(),
      },
      {
        name: 'period_name',
        type: 'Query',
        schema: z.enum(['week', 'month', 'quarter', 'year', 'custom']).optional(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/orders/cancel',
    alias: 'cancel_fulfillment_v2_clients_orders_cancel_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CancelFulfillmentInputDTO,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/customer-io/messages',
    alias: 'get_customer_io_messages_by_email_v2_clients_orders_customer_io_messages_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'email',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: z.array(CustomerIoMessageSchema),
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/dashboard',
    alias: 'dashboard_v2_clients_orders_dashboard_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'end_date',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'period_in_days',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'period_name',
        type: 'Query',
        schema: z.enum(['week', 'month', 'quarter', 'year', 'custom']).optional(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/event-count',
    alias: 'results_count_v2_clients_orders_event_count_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'end_date',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'period_in_days',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'time_zone',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: z.record(z.string(), z.number().int()),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/insurance-plans',
    alias: 'get_insurance_plans_v2_clients_orders_insurance_plans_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'lab_id',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: z
      .object({ items: z.union([z.array(InsuranceItem), z.null()]) })
      .partial()
      .loose(),
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/partner-config',
    alias: 'get_order_creation_client_configuration_v2_clients_orders_partner_config_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: OrderClientConfigurationSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/providers',
    alias: 'get_order_providers_v2_clients_orders_providers_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: z.array(ProviderSchema),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/rejections',
    alias: 'get_kit_rejections_v2_clients_orders_rejections_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'date_from',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'date_to',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'rejectionType',
        type: 'Query',
        schema: z.enum(['full', 'partial', 'all']).optional(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: KitRejectionsSummarySchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/results/:kit_id',
    alias: 'results_by_kit_id_v2_clients_orders_results__kit_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'full_history',
        type: 'Query',
        schema: full_history,
      },
      {
        name: 'exclude',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: z
      .object({
        kitId: z.string(),
        partnerId: z.string(),
        labName: z.union([z.string(), z.null()]).optional(),
        labInfo: z.union([KitOrderLabInfoSchema, z.null()]).optional(),
        pdf: z.union([z.string(), z.null()]).optional(),
        pdfEntities: z.union([z.array(z.object({}).partial().loose()), z.null()]).optional(),
        pdfs: z.union([z.array(z.union([z.string(), z.null()])), z.null()]).optional(),
        results: z.array(KitOrderResultLineWithCorrectionSchema),
        dateReported: z.union([z.string(), z.null()]).optional(),
        providerInfo: ProviderInfoOutput,
        inboundTrackingUrl: z.union([z.string(), z.null()]).optional(),
        outboundTrackingUrl: z.union([z.string(), z.null()]).optional(),
      })
      .loose(),
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/orders/skus',
    alias: 'get_skus_v2_clients_orders_skus_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().optional().default(1),
      },
      {
        name: 'page_size',
        type: 'Query',
        schema: z.number().int().optional().default(20),
      },
      {
        name: 'is_test_order',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: PaginatedResponseSchema_SkuSchema_,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/users',
    alias: 'get_client_user_list_v2_clients_users_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'records_per_page',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(10),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().gte(1).optional().default(1),
      },
      {
        name: 'search_string',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'is_active',
        type: 'Query',
        schema: full_history,
      },
      {
        name: 'sort_by_name',
        type: 'Query',
        schema: sort_by_name,
      },
      {
        name: 'sort_by_status',
        type: 'Query',
        schema: sort_by_name,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: PaginatedResponseSchema_ClientUserProfileListSchema_,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v2/clients/users',
    alias: 'delete_client_user_v2_clients_users_delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteClientUserDTO,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/users/:user_id/impersonate',
    alias: 'impersonate_client_user_v2_clients_users__user_id__impersonate_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: ImpersonateClientUserResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 502,
        description: `Bad Gateway`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 503,
        description: `Service Unavailable`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/users/export',
    alias: 'export_client_users_csv_v2_clients_users_export_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'search_string',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'is_active',
        type: 'Query',
        schema: full_history,
      },
      {
        name: 'sort_by_name',
        type: 'Query',
        schema: sort_by_name,
      },
      {
        name: 'sort_by_status',
        type: 'Query',
        schema: sort_by_name,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/users/invitation-reminders',
    alias: 'get_client_invitation_reminders_v2_clients_users_invitation_reminders_get',
    requestFormat: 'json',
    response: z.array(ClientInvitationReminderSchema),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/clients/users/invitation-reminders/send',
    alias: 'send_client_invitation_reminder_v2_clients_users_invitation_reminders_send_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendClientInvitationReminderDTO,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/users/me',
    alias: 'profile_v2_clients_users_me_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: ClientUserProfileSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v2/clients/users/me',
    alias: 'update_profile_v2_clients_users_me_patch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateClientUserProfileDTO,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'x-auth-provider',
        type: 'Header',
        schema: partner_id,
      },
      {
        name: 'x-auth-provider-record-id',
        type: 'Header',
        schema: partner_id,
      },
    ],
    response: ClientUserProfileSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/users/me/partners',
    alias: 'partners_v2_clients_users_me_partners_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: ClientUserWithPartnersSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/clients/users/mfa-status',
    alias: 'get_client_user_mfa_status_v2_clients_users_mfa_status_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'user_id',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: ClientUserMfaStatusSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/feature-flags',
    alias: 'get_feature_flags_v2_feature_flags_get',
    requestFormat: 'json',
    response: FeatureFlagsResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/kit/correction/:secret',
    alias: 'get_kit_address_update_v2_kit_correction__secret__get',
    description: `Look up a kit address correction request for the Patient Portal.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'secret',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: KitAddressUpdateLookupResponse,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/kit/correction/:secret',
    alias: 'submit_kit_address_update_v2_kit_correction__secret__post',
    description: `Submit a corrected address and place a replacement order for the Patient Portal.

Intentionally unauthenticated: &#x60;secret&#x60; in the path is the sole access control. It is never
logged by this router or its use case. The correction record is only written after the
replacement order is placed successfully; a failed placement is not retried.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddressUpdateSubmitDTO,
      },
      {
        name: 'secret',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.object({ replacement_kit_id: z.string() }).loose(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 404,
        description: `Not Found`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 503,
        description: `Service Unavailable`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/kit/correction/:secret/identity',
    alias: 'confirm_kit_address_identity_v2_kit_correction__secret__identity_post',
    description: `Confirm the member&#x27;s name and date of birth before the Patient Portal shows the current address.

Intentionally unauthenticated: &#x60;secret&#x60; in the path is the sole access control. The submitted name and date
of birth and the previous address are never logged by this router or its use case.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ConfirmKitAddressIdentityDTO,
      },
      {
        name: 'secret',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: KitAddressUpdateIdentityResponse,
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 404,
        description: `Not Found`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 503,
        description: `Service Unavailable`,
        schema: HTTPCustomExceptionSchema,
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/kit/correction/:secret/suggestions',
    alias: 'suggest_kit_address_correction_v2_kit_correction__secret__suggestions_post',
    description: `Suggest United States addresses on the address step of a kit address correction.

Intentionally unauthenticated: &#x60;secret&#x60; in the path is the sole access control. The link is checked before the
address provider is called, so an unknown or used link never reaches the provider. Only &#x60;query&#x60; is read from the
body, and the country is always USA. The query is a street address, so neither the query nor the secret is logged.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddressAutocompleteRequestDTO,
      },
      {
        name: 'secret',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: AddressAutocompleteResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 404,
        description: `Not Found`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 503,
        description: `Service Unavailable`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/kits/:kit_id/eligibility-questionnaire',
    alias: 'save_eligibility_questionnaire_v2_kits__kit_id__eligibility_questionnaire_post',
    description: `Save eligibility questionnaire responses during kit registration.

Intentionally unprotected at the auth-gateway layer for the live /v2/kits/... path (same
pattern as /kits/validate): PAS registers users.me.kits.eligibility-questionnaire.create
on /users/me/kits/... so pre-signup Stytch sessions are not blocked. Stytch session and
order-api kit validation are enforced in portal-api.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateEligibilityQuestionnaireDTO,
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: EligibilityQuestionnaireResponsesSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/kits/:kit_id/order-primary-care-provider',
    alias: 'get_order_primary_care_provider_v2_kits__kit_id__order_primary_care_provider_post',
    description: `Fetch order PCP for kit registration when the user has a Stytch session but may not exist in PAS yet.

Intentionally unprotected at the auth-gateway layer (like /kits/validate); Stytch session and order patient
verification are enforced in portal-api.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PatientPrimaryCareVerificationDTO,
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrderAutofillResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/kits/:kit_id/pcp-fax-info',
    alias: 'get_kit_pcp_info_v2_kits__kit_id__pcp_fax_info_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: KitPCPSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: HTTPCustomExceptionSchema,
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/kits/validate',
    alias: 'validate_kit_v2_kits_validate_post',
    description: `Validate a kit for the Patient Portal.

Called when the user has a Stytch session but has not signed up in our system yet. The caught exceptions match
the ones thrown by register_kits in users_router_v2.py`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ kit_id: z.string() }).loose(),
      },
    ],
    response: KitValidationResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 409,
        description: `Conflict`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/users/me',
    alias: 'profile_v2_users_me_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: PatientUserModelExtendedSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v2/users/me',
    alias: 'update_profile_v2_users_me_patch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateUserDTO,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: PatientUserModelSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v2/users/me',
    alias: 'delete_patient_user_v2_users_me_delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 409,
        description: `Conflict`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/users/me/hipaa-consent',
    alias: 'save_hipaa_consent_v2_users_me_hipaa_consent_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateHipaaConsentDTO,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: HipaaConsentSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/users/me/kits',
    alias: 'get_kits_v2_users_me_kits_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'records_per_page',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(10),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().gte(0).optional().default(0),
      },
      {
        name: 'exclude',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'kit_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: PaginatedResponseSchema_KitWithResultsSchema_,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/users/me/kits',
    alias: 'register_kits_v2_users_me_kits_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RegisterKitDTO,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: UserKitSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.union([HTTPExceptionSchema, HTTPCustomExceptionSchema]),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 409,
        description: `Conflict`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/users/me/kits/:kit_id/tracking',
    alias: 'get_kit_tracking_urls_v2_users_me_kits__kit_id__tracking_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: KitTrackingUrlsSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/address',
    alias: 'autocomplete_address_v3_address_post',
    description: `Get address suggestions for autocomplete.

Open at the auth-gateway (like kit validate) so first-time Patient Portal registration
users with a Stytch session can call it. Auth is the Stytch session cookie only;
X-User-Identity is not trusted on this route.

Returns display labels, place IDs, and structured address components.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddressAutocompleteRequestDTO,
      },
    ],
    response: AddressAutocompleteResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 503,
        description: `Service Unavailable`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/auth/client/invite',
    alias: 'client_invite_v3_auth_client_invite_post',
    description: `New version of the client invite endpoint. This endpoint is used to invite a new user
to the client portal via Stytch.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ClientInviteDTO,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'email_display_name',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'x-auth-provider',
        type: 'Header',
        schema: z.string(),
      },
      {
        name: 'x-auth-provider-record-id',
        type: 'Header',
        schema: z.string(),
      },
    ],
    response: ClientUserAfterInviteSchema,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/auth/client/reset-mfa',
    alias: 'reset_client_user_mfa_v3_auth_client_reset_mfa_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/auth/client/sign-up',
    alias: 'client_sign_up_v3_auth_client_sign_up_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ email: z.string() }).loose(),
      },
      {
        name: 'x-auth-provider',
        type: 'Header',
        schema: z.string(),
      },
      {
        name: 'x-auth-provider-record-id',
        type: 'Header',
        schema: z.string(),
      },
    ],
    response: ClientUserWithUserIdSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 409,
        description: `Conflict`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/auth/sign-up',
    alias: 'patient_sign_up_v3_auth_sign_up_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PatientSignUpStytchDTO,
      },
      {
        name: 'x-auth-provider',
        type: 'Header',
        schema: z.string(),
      },
      {
        name: 'x-auth-provider-record-id',
        type: 'Header',
        schema: z.string(),
      },
    ],
    response: PatientUserExtendedSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 409,
        description: `Conflict`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/auth/sign-up-with-kit-and-hipaa-consent',
    alias:
      'patient_sign_up_with_kit_and_hipaa_consent_v3_auth_sign_up_with_kit_and_hipaa_consent_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RegisterPatientUserWithKitAndHipaaConsentDTO,
      },
      {
        name: 'x-auth-provider',
        type: 'Header',
        schema: z.string(),
      },
      {
        name: 'x-auth-provider-record-id',
        type: 'Header',
        schema: z.string(),
      },
    ],
    response: RegisterPatientUserWithKitAndHipaaConsentResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.union([HTTPExceptionSchema, HTTPCustomExceptionSchema]),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 409,
        description: `Conflict`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v3/clients/analytics/capabilities',
    alias: 'get_analytics_capabilities_v3_clients_analytics_capabilities_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'partner_ids',
        type: 'Query',
        schema: z.array(z.string().min(1).max(100)).min(1).max(20),
      },
    ],
    response: AnalyticsCapabilities,
    errors: [
      {
        status: 403,
        description: `Access denied`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/clients/analytics/chart-question',
    alias: 'answer_analytics_question_v3_clients_analytics_chart_question_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AnswerAnalyticsQuestionRequestDTO,
      },
      {
        name: 'accept',
        type: 'Header',
        schema: z.string().optional().default('application/json'),
      },
    ],
    response: AnalyticsResult,
    errors: [
      {
        status: 403,
        description: `Partner access denied`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Question cannot be answered`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Request quota exceeded`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 503,
        description: `Analytics dependency unavailable`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v3/clients/analytics/pinned-charts',
    alias: 'list_pinned_charts_v3_clients_analytics_pinned_charts_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'partner_ids',
        type: 'Query',
        schema: z.array(z.string().min(1).max(100)).min(1).max(20),
      },
    ],
    response: z.array(PinnedChartResponseSchema),
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/clients/analytics/pinned-charts',
    alias: 'create_pinned_chart_v3_clients_analytics_pinned_charts_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePinnedChartRequestDTO,
      },
    ],
    response: PinnedChartResponseSchema,
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 409,
        description: `Conflict`,
        schema: PinnedChartCapacityErrorSchema,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v3/clients/analytics/pinned-charts/:pinned_chart_id',
    alias: 'delete_pinned_chart_v3_clients_analytics_pinned_charts__pinned_chart_id__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'pinned_chart_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: z.array(z.string().min(1).max(100)).min(1).max(20),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v3/clients/analytics/question-history',
    alias: 'list_question_history_v3_clients_analytics_question_history_get',
    requestFormat: 'json',
    response: z.array(QuestionHistoryEntryResponseSchema),
  },
  {
    method: 'post',
    path: '/v3/clients/analytics/question-history',
    alias: 'record_question_history_v3_clients_analytics_question_history_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ question: z.string().min(1).max(500) }),
      },
    ],
    response: z.array(QuestionHistoryEntryResponseSchema),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/v3/clients/analytics/question-history',
    alias: 'clear_question_history_v3_clients_analytics_question_history_delete',
    requestFormat: 'json',
    response: z.array(QuestionHistoryEntryResponseSchema),
  },
  {
    method: 'delete',
    path: '/v3/clients/analytics/question-history/:entry_id',
    alias: 'delete_question_history_entry_v3_clients_analytics_question_history__entry_id__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'entry_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.array(QuestionHistoryEntryResponseSchema),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/clients/eligibility-questionnaire/edit',
    alias: 'edit_eligibility_questionnaire_v3_clients_eligibility_questionnaire_edit_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditEligibilityQuestionnaireRequestDTO,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: EditEligibilityQuestionnaireResponseDTO,
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 429,
        description: `Too Many Requests`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 502,
        description: `Bad Gateway`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/clients/orders/:kit_id/corrections',
    alias: 'request_test_order_corrections_v3_clients_orders__kit_id__corrections_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RequestMultipleTestOrderCorrectionsDTO,
      },
      {
        name: 'kit_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: RequestMultipleTestOrderCorrectionsSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v3/clients/orders/aggregated-data',
    alias: 'get_aggregated_data_v3_clients_orders_aggregated_data_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'start_date',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'end_date',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'aggregation_period',
        type: 'Query',
        schema: z.enum(['daily', 'weekly', 'monthly', 'previous', 'current']).optional(),
      },
      {
        name: 'time_zone',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'status_keys',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: GetAggregatedDataSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v3/clients/orders/aoe-questionnaire',
    alias: 'get_aoe_questionnaire_v3_clients_orders_aoe_questionnaire_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'order_sku',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: QuestionnaireDTO,
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v3/clients/orders/by-state',
    alias: 'get_orders_by_state_v3_clients_orders_by_state_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'start_date',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'end_date',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'time_zone',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: GetOrdersByStateSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/clients/orders/completion',
    alias: 'set_completion_v3_clients_orders_completion_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetCompletionStatusDTO,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: z.object({}).partial().loose(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/clients/orders/pcp-info',
    alias: 'get_batch_client_order_pcp_info_v3_clients_orders_pcp_info_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BatchClientOrderPCPInfoRequestDTO,
      },
    ],
    response: BatchClientOrderPCPInfoResponseSchema,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v3/clients/orders/recent',
    alias: 'get_most_recent_orders_for_partner_v3_clients_orders_recent_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'sort_by_field',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'search_field',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'search_keyword',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'current_status',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'has_abnormal_results',
        type: 'Query',
        schema: full_history,
      },
      {
        name: 'automatically_registered',
        type: 'Query',
        schema: full_history,
      },
      {
        name: 'rejection_reason',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'completed',
        type: 'Query',
        schema: full_history,
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().gte(1).optional().default(1),
      },
      {
        name: 'page_size',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(25),
      },
    ],
    response: OrderPaginatedSchema,
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/clients/orders/recent',
    alias: 'search_most_recent_orders_for_partner_v3_clients_orders_recent_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RecentOrdersSearchRequestDTO,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: OrderPaginatedSchema,
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v3/clients/orders/time-metrics',
    alias: 'get_time_metrics_v3_clients_orders_time_metrics_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'start_date',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'end_date',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'metrics',
        type: 'Query',
        schema: z.array(z.enum(['turnaround_time', 'lab_processing_time'])),
      },
      {
        name: 'include_weekends',
        type: 'Query',
        schema: full_history,
      },
      {
        name: 'days_threshold_turnaround',
        type: 'Query',
        schema: days_threshold_turnaround,
      },
      {
        name: 'days_threshold_lab_processing',
        type: 'Query',
        schema: days_threshold_turnaround,
      },
      {
        name: 'percentile',
        type: 'Query',
        schema: percentile,
      },
      {
        name: 'time_zone',
        type: 'Query',
        schema: partner_id,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
    ],
    response: TimeMetricsSchema,
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/clients/program-analytics',
    alias: 'get_program_analytics_v3_clients_program_analytics_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ProgramAnalyticsRequestDTO,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: ProgramAnalyticsResponseSchema,
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v3/tools/ai-agent/question',
    alias: 'ask_question_v3_tools_ai_agent_question_post',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Body_ask_question_v3_tools_ai_agent_question_post,
      },
      {
        name: 'partner_ids',
        type: 'Query',
        schema: partner_ids,
      },
      {
        name: 'partner_id',
        type: 'Query',
        schema: partner_id,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.object({ detail: z.string() }).loose(),
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: z.object({ detail: z.string() }).loose(),
      },
    ],
  },
])

// Schema type exports
export type UpdateEmailDTOType = z.infer<typeof schemas.UpdateEmailDTO>
export type partner_idsType = z.infer<typeof schemas.partner_ids>
export type partner_idType = z.infer<typeof schemas.partner_id>
export type UserSchemaType = z.infer<typeof schemas.UserSchema>
export type HTTPExceptionSchemaType = z.infer<typeof schemas.HTTPExceptionSchema>
export type ValidationErrorType = z.infer<typeof schemas.ValidationError>
export type HTTPValidationErrorType = z.infer<typeof schemas.HTTPValidationError>
export type UserDiscoveryDTOType = z.infer<typeof schemas.UserDiscoveryDTO>
export type UserDiscoverySchemaType = z.infer<typeof schemas.UserDiscoverySchema>
export type OrgClientSchemaType = z.infer<typeof schemas.OrgClientSchema>
export type OrgSchemaType = z.infer<typeof schemas.OrgSchema>
export type IdentityInfoSchemaType = z.infer<typeof schemas.IdentityInfoSchema>
export type FileAccessFlagValueType = z.infer<typeof schemas.FileAccessFlagValue>
export type ClientFileStatusType = z.infer<typeof schemas.ClientFileStatus>
export type ClientFileSchemaType = z.infer<typeof schemas.ClientFileSchema>
export type PartnerReportFileSchemaType = z.infer<typeof schemas.PartnerReportFileSchema>
export type PartnerReportsSchemaType = z.infer<typeof schemas.PartnerReportsSchema>
export type ClientFilesResponseSchemaType = z.infer<typeof schemas.ClientFilesResponseSchema>
export type Body_upload_client_file_v2_clients_files_postType = z.infer<typeof schemas.Body_upload_client_file_v2_clients_files_post>
export type SignedFileUrlSchemaType = z.infer<typeof schemas.SignedFileUrlSchema>
export type ClientFileQuarantinedErrorSchemaType = z.infer<typeof schemas.ClientFileQuarantinedErrorSchema>
export type full_historyType = z.infer<typeof schemas.full_history>
export type KitOrderLabInfoSchemaType = z.infer<typeof schemas.KitOrderLabInfoSchema>
export type KitOrderResultLineSchemaType = z.infer<typeof schemas.KitOrderResultLineSchema>
export type KitOrderResultLineWithCorrectionSchemaType = z.infer<typeof schemas.KitOrderResultLineWithCorrectionSchema>
export type ProviderInfoOutputType = z.infer<typeof schemas.ProviderInfoOutput>
export type ResultsByKitIdSchemaType = z.infer<typeof schemas.ResultsByKitIdSchema>
export type get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_BodyType = z.infer<typeof schemas.get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get_Body>
export type OderDetailsSectionSchemaType = z.infer<typeof schemas.OderDetailsSectionSchema>
export type ShippingInfoSchemaType = z.infer<typeof schemas.ShippingInfoSchema>
export type TrackingInfoSchemaType = z.infer<typeof schemas.TrackingInfoSchema>
export type KitOrderResultCodeSchemaType = z.infer<typeof schemas.KitOrderResultCodeSchema>
export type KitOrderCodeSchemaType = z.infer<typeof schemas.KitOrderCodeSchema>
export type SingleEventSchemaType = z.infer<typeof schemas.SingleEventSchema>
export type OrderAdditionalFieldEntrySchemaType = z.infer<typeof schemas.OrderAdditionalFieldEntrySchema>
export type ResultRejectionInfoSchemaType = z.infer<typeof schemas.ResultRejectionInfoSchema>
export type AoeQuestionOptionSchemaType = z.infer<typeof schemas.AoeQuestionOptionSchema>
export type AoeQuestionAnswerSchemaType = z.infer<typeof schemas.AoeQuestionAnswerSchema>
export type SampleCollectionTypeSchemaType = z.infer<typeof schemas.SampleCollectionTypeSchema>
export type SampleCollectionConfigurationSchemaType = z.infer<typeof schemas.SampleCollectionConfigurationSchema>
export type OrderDetailsWithShippingAndResultsSchemaType = z.infer<typeof schemas.OrderDetailsWithShippingAndResultsSchema>
export type PatientProvidedPCPSchemaType = z.infer<typeof schemas.PatientProvidedPCPSchema>
export type OrderPrimaryCareProviderAddressSchemaType = z.infer<typeof schemas.OrderPrimaryCareProviderAddressSchema>
export type OrderPrimaryCareProviderSchemaType = z.infer<typeof schemas.OrderPrimaryCareProviderSchema>
export type ClientOrderPCPInfoSchemaType = z.infer<typeof schemas.ClientOrderPCPInfoSchema>
export type EligibilityQuestionnaireResponseSchemaType = z.infer<typeof schemas.EligibilityQuestionnaireResponseSchema>
export type EligibilityQuestionnaireResponsesSchemaType = z.infer<typeof schemas.EligibilityQuestionnaireResponsesSchema>
export type OrderAPIOrderEventsType = z.infer<typeof schemas.OrderAPIOrderEvents>
export type ShippingAddressDTOType = z.infer<typeof schemas.ShippingAddressDTO>
export type PatientAddressDTOType = z.infer<typeof schemas.PatientAddressDTO>
export type PatientInfoDTOType = z.infer<typeof schemas.PatientInfoDTO>
export type ProviderInfoDTOType = z.infer<typeof schemas.ProviderInfoDTO>
export type FulfillmentDTOType = z.infer<typeof schemas.FulfillmentDTO>
export type InsuranceDTOType = z.infer<typeof schemas.InsuranceDTO>
export type DesiredTestResultDTOType = z.infer<typeof schemas.DesiredTestResultDTO>
export type SetTestOrderStatusDTOType = z.infer<typeof schemas.SetTestOrderStatusDTO>
export type SetTestOrderStatusSchemaType = z.infer<typeof schemas.SetTestOrderStatusSchema>
export type AddKitEventDTOType = z.infer<typeof schemas.AddKitEventDTO>
export type AddKitEventSchemaType = z.infer<typeof schemas.AddKitEventSchema>
export type RequestTestOrderCorrectionDTOType = z.infer<typeof schemas.RequestTestOrderCorrectionDTO>
export type RequestTestOrderCorrectionSchemaType = z.infer<typeof schemas.RequestTestOrderCorrectionSchema>
export type KitValidationResponseSchemaType = z.infer<typeof schemas.KitValidationResponseSchema>
export type ClientRegisterKitPatientInfoDTOType = z.infer<typeof schemas.ClientRegisterKitPatientInfoDTO>
export type ClientRegisterKitPatientAddressDTOType = z.infer<typeof schemas.ClientRegisterKitPatientAddressDTO>
export type ConsentAgreementTypeType = z.infer<typeof schemas.ConsentAgreementType>
export type ConsentAgreementSourceType = z.infer<typeof schemas.ConsentAgreementSource>
export type ClientRegisterKitConsentAgreementDTOType = z.infer<typeof schemas.ClientRegisterKitConsentAgreementDTO>
export type EligibilityQuestionnaireItemDTOType = z.infer<typeof schemas.EligibilityQuestionnaireItemDTO>
export type ClientRegisterKitPcpContactDTOType = z.infer<typeof schemas.ClientRegisterKitPcpContactDTO>
export type ClientRegisterKitPcpDTOType = z.infer<typeof schemas.ClientRegisterKitPcpDTO>
export type ClientRegisterKitDTOType = z.infer<typeof schemas.ClientRegisterKitDTO>
export type KitRegistrationResponseSchemaType = z.infer<typeof schemas.KitRegistrationResponseSchema>
export type CustomerIoMessageCustomerIdentifierEntityType = z.infer<typeof schemas.CustomerIoMessageCustomerIdentifierEntity>
export type CustomerIoMessageMetricsEntityType = z.infer<typeof schemas.CustomerIoMessageMetricsEntity>
export type CustomerIoMessageSchemaType = z.infer<typeof schemas.CustomerIoMessageSchema>
export type OrderClientConfigurationSchemaType = z.infer<typeof schemas.OrderClientConfigurationSchema>
export type ResultCodeTypeType = z.infer<typeof schemas.ResultCodeType>
export type ResultCodeWithReferenceRangesSchemaType = z.infer<typeof schemas.ResultCodeWithReferenceRangesSchema>
export type LabCodeSchemaType = z.infer<typeof schemas.LabCodeSchema>
export type SkuRejectionReasonSchemaType = z.infer<typeof schemas.SkuRejectionReasonSchema>
export type SkuSchemaType = z.infer<typeof schemas.SkuSchema>
export type PaginatedResponseSchema_SkuSchema_Type = z.infer<typeof schemas.PaginatedResponseSchema_SkuSchema_>
export type RejectionReasonSummarySchemaType = z.infer<typeof schemas.RejectionReasonSummarySchema>
export type KitRejectionsSummarySchemaType = z.infer<typeof schemas.KitRejectionsSummarySchema>
export type OrderAdditionalFieldSchemaType = z.infer<typeof schemas.OrderAdditionalFieldSchema>
export type ProviderSchemaType = z.infer<typeof schemas.ProviderSchema>
export type CancelFulfillmentInputDTOType = z.infer<typeof schemas.CancelFulfillmentInputDTO>
export type NpiType = z.infer<typeof schemas.Npi>
export type PrimaryCareProviderAddressType = z.infer<typeof schemas.PrimaryCareProviderAddress>
export type FaxType = z.infer<typeof schemas.Fax>
export type PrimaryCareProviderType = z.infer<typeof schemas.PrimaryCareProvider>
export type PlaceOrderAoeAnswerDTOType = z.infer<typeof schemas.PlaceOrderAoeAnswerDTO>
export type LabBillingTypeType = z.infer<typeof schemas.LabBillingType>
export type InsuranceAddressType = z.infer<typeof schemas.InsuranceAddress>
export type InsuranceInfoType = z.infer<typeof schemas.InsuranceInfo>
export type PlaceOrderDTOType = z.infer<typeof schemas.PlaceOrderDTO>
export type partner_id__2Type = z.infer<typeof schemas.partner_id__2>
export type PlacedOrderSchemaType = z.infer<typeof schemas.PlacedOrderSchema>
export type InsuranceItemType = z.infer<typeof schemas.InsuranceItem>
export type InsuranceListResponseType = z.infer<typeof schemas.InsuranceListResponse>
export type ContinueKitInvestigationDTOType = z.infer<typeof schemas.ContinueKitInvestigationDTO>
export type ClientUserProfileSchemaType = z.infer<typeof schemas.ClientUserProfileSchema>
export type UpdateClientUserProfileDTOType = z.infer<typeof schemas.UpdateClientUserProfileDTO>
export type FieldSortingType = z.infer<typeof schemas.FieldSorting>
export type sort_by_nameType = z.infer<typeof schemas.sort_by_name>
export type ProgramDTOType = z.infer<typeof schemas.ProgramDTO>
export type ClientUserProfileListSchemaType = z.infer<typeof schemas.ClientUserProfileListSchema>
export type PaginatedResponseSchema_ClientUserProfileListSchema_Type = z.infer<typeof schemas.PaginatedResponseSchema_ClientUserProfileListSchema_>
export type DeleteClientUserDTOType = z.infer<typeof schemas.DeleteClientUserDTO>
export type ClientInvitationReminderSchemaType = z.infer<typeof schemas.ClientInvitationReminderSchema>
export type SendClientInvitationReminderDTOType = z.infer<typeof schemas.SendClientInvitationReminderDTO>
export type ClientUserMfaStatusSchemaType = z.infer<typeof schemas.ClientUserMfaStatusSchema>
export type FeatureFlagSchemaType = z.infer<typeof schemas.FeatureFlagSchema>
export type ProgramStatusType = z.infer<typeof schemas.ProgramStatus>
export type PartnerSchemaType = z.infer<typeof schemas.PartnerSchema>
export type ClientUserWithPartnersSchemaType = z.infer<typeof schemas.ClientUserWithPartnersSchema>
export type ImpersonateClientUserResponseSchemaType = z.infer<typeof schemas.ImpersonateClientUserResponseSchema>
export type FeatureFlagsResponseSchemaType = z.infer<typeof schemas.FeatureFlagsResponseSchema>
export type infrastructure__schemas__user_schemas__PrimaryCareProviderSchemaType = z.infer<typeof schemas.infrastructure__schemas__user_schemas__PrimaryCareProviderSchema>
export type GetCustomFieldInUserSchemaType = z.infer<typeof schemas.GetCustomFieldInUserSchema>
export type GetUserCustomFieldInUserSchemaType = z.infer<typeof schemas.GetUserCustomFieldInUserSchema>
export type HipaaConsentInfoSchemaType = z.infer<typeof schemas.HipaaConsentInfoSchema>
export type PatientUserModelExtendedSchemaType = z.infer<typeof schemas.PatientUserModelExtendedSchema>
export type PrimaryCareProviderDTOType = z.infer<typeof schemas.PrimaryCareProviderDTO>
export type UpdateUserDTOType = z.infer<typeof schemas.UpdateUserDTO>
export type PatientUserModelSchemaType = z.infer<typeof schemas.PatientUserModelSchema>
export type PrimaryCareProviderEntityType = z.infer<typeof schemas.PrimaryCareProviderEntity>
export type KitOrderTestsWithProviderSchemaType = z.infer<typeof schemas.KitOrderTestsWithProviderSchema>
export type KitHistoricalEventDTOType = z.infer<typeof schemas.KitHistoricalEventDTO>
export type KitOrderRejectionSchemaType = z.infer<typeof schemas.KitOrderRejectionSchema>
export type KitWithResultsSchemaType = z.infer<typeof schemas.KitWithResultsSchema>
export type PaginatedResponseSchema_KitWithResultsSchema_Type = z.infer<typeof schemas.PaginatedResponseSchema_KitWithResultsSchema_>
export type PatientConsentRequestDTOType = z.infer<typeof schemas.PatientConsentRequestDTO>
export type RegisterKitDTOType = z.infer<typeof schemas.RegisterKitDTO>
export type UserKitSchemaType = z.infer<typeof schemas.UserKitSchema>
export type HTTPCustomExceptionSchemaType = z.infer<typeof schemas.HTTPCustomExceptionSchema>
export type KitTrackingUrlsSchemaType = z.infer<typeof schemas.KitTrackingUrlsSchema>
export type CreateHipaaConsentDTOType = z.infer<typeof schemas.CreateHipaaConsentDTO>
export type HipaaConsentSchemaType = z.infer<typeof schemas.HipaaConsentSchema>
export type ValidateKitDTOType = z.infer<typeof schemas.ValidateKitDTO>
export type SexAtBirthType = z.infer<typeof schemas.SexAtBirth>
export type PatientPrimaryCareVerificationDTOType = z.infer<typeof schemas.PatientPrimaryCareVerificationDTO>
export type OrderAutofillPCPAddressSchemaType = z.infer<typeof schemas.OrderAutofillPCPAddressSchema>
export type OrderAutofillPatientAddressSchemaType = z.infer<typeof schemas.OrderAutofillPatientAddressSchema>
export type OrderAutofillResponseSchemaType = z.infer<typeof schemas.OrderAutofillResponseSchema>
export type CreateEligibilityQuestionnaireDTOType = z.infer<typeof schemas.CreateEligibilityQuestionnaireDTO>
export type KitPCPSchemaType = z.infer<typeof schemas.KitPCPSchema>
export type KitAddressUpdateLookupResponseType = z.infer<typeof schemas.KitAddressUpdateLookupResponse>
export type AddressUpdateSubmitDTOType = z.infer<typeof schemas.AddressUpdateSubmitDTO>
export type KitAddressUpdateSubmitResponseType = z.infer<typeof schemas.KitAddressUpdateSubmitResponse>
export type ConfirmKitAddressIdentityDTOType = z.infer<typeof schemas.ConfirmKitAddressIdentityDTO>
export type KitAddressUpdatePreviousAddressResponseType = z.infer<typeof schemas.KitAddressUpdatePreviousAddressResponse>
export type KitAddressUpdateIdentityResponseType = z.infer<typeof schemas.KitAddressUpdateIdentityResponse>
export type AddressAutocompleteRequestDTOType = z.infer<typeof schemas.AddressAutocompleteRequestDTO>
export type AddressComponentSchemaType = z.infer<typeof schemas.AddressComponentSchema>
export type AddressSuggestionSchemaType = z.infer<typeof schemas.AddressSuggestionSchema>
export type AddressAutocompleteResponseSchemaType = z.infer<typeof schemas.AddressAutocompleteResponseSchema>
export type PatientSignUpStytchDTOType = z.infer<typeof schemas.PatientSignUpStytchDTO>
export type GetCustomFieldInUserEntityType = z.infer<typeof schemas.GetCustomFieldInUserEntity>
export type GetUserCustomFieldInUserEntityType = z.infer<typeof schemas.GetUserCustomFieldInUserEntity>
export type infrastructure__schemas__common__PrimaryCareProviderSchemaType = z.infer<typeof schemas.infrastructure__schemas__common__PrimaryCareProviderSchema>
export type PatientUserExtendedSchemaType = z.infer<typeof schemas.PatientUserExtendedSchema>
export type RegisterPatientUserWithKitAndHipaaConsentDTOType = z.infer<typeof schemas.RegisterPatientUserWithKitAndHipaaConsentDTO>
export type RegisterPatientUserWithKitAndHipaaConsentResponseSchemaType = z.infer<typeof schemas.RegisterPatientUserWithKitAndHipaaConsentResponseSchema>
export type RoleType = z.infer<typeof schemas.Role>
export type ClientInviteDTOType = z.infer<typeof schemas.ClientInviteDTO>
export type ClientUserWithUserIdSchemaType = z.infer<typeof schemas.ClientUserWithUserIdSchema>
export type ClientUserAfterInviteSchemaType = z.infer<typeof schemas.ClientUserAfterInviteSchema>
export type ClientSignUpInStytchFlowDTOType = z.infer<typeof schemas.ClientSignUpInStytchFlowDTO>
export type AnalyticsDimensionCapabilityType = z.infer<typeof schemas.AnalyticsDimensionCapability>
export type AnalyticsMetricCapabilityType = z.infer<typeof schemas.AnalyticsMetricCapability>
export type AnalyticsRelationCapabilityType = z.infer<typeof schemas.AnalyticsRelationCapability>
export type AnalyticsCapabilitiesType = z.infer<typeof schemas.AnalyticsCapabilities>
export type AnswerAnalyticsQuestionRequestDTOType = z.infer<typeof schemas.AnswerAnalyticsQuestionRequestDTO>
export type AnalyticsChartMetadataType = z.infer<typeof schemas.AnalyticsChartMetadata>
export type AnalyticsTimeframeType = z.infer<typeof schemas.AnalyticsTimeframe>
export type KpiVisualizationType = z.infer<typeof schemas.KpiVisualization>
export type BreakdownBarValueType = z.infer<typeof schemas.BreakdownBarValue>
export type BreakdownBarCategoryType = z.infer<typeof schemas.BreakdownBarCategory>
export type BreakdownBarVisualizationType = z.infer<typeof schemas.BreakdownBarVisualization>
export type PieSegmentType = z.infer<typeof schemas.PieSegment>
export type PieVisualizationType = z.infer<typeof schemas.PieVisualization>
export type DonutSegmentType = z.infer<typeof schemas.DonutSegment>
export type DonutVisualizationType = z.infer<typeof schemas.DonutVisualization>
export type TimeSeriesPointType = z.infer<typeof schemas.TimeSeriesPoint>
export type TimeSeriesSeriesType = z.infer<typeof schemas.TimeSeriesSeries>
export type ThresholdType = z.infer<typeof schemas.Threshold>
export type ThresholdTimeSeriesVisualizationType = z.infer<typeof schemas.ThresholdTimeSeriesVisualization>
export type AnalyticsCategoryType = z.infer<typeof schemas.AnalyticsCategory>
export type CombinationValueType = z.infer<typeof schemas.CombinationValue>
export type CombinationSeriesType = z.infer<typeof schemas.CombinationSeries>
export type CombinationVisualizationType = z.infer<typeof schemas.CombinationVisualization>
export type TableVisualizationType = z.infer<typeof schemas.TableVisualization>
export type AnalyticsTableColumnType = z.infer<typeof schemas.AnalyticsTableColumn>
export type AnalyticsTableRowType = z.infer<typeof schemas.AnalyticsTableRow>
export type AnalyticsTableModelType = z.infer<typeof schemas.AnalyticsTableModel>
export type AnalyticsResultType = z.infer<typeof schemas.AnalyticsResult>
export type PinnedChartResponseSchemaType = z.infer<typeof schemas.PinnedChartResponseSchema>
export type CreatePinnedChartRequestDTOType = z.infer<typeof schemas.CreatePinnedChartRequestDTO>
export type PinnedChartCapacityErrorSchemaType = z.infer<typeof schemas.PinnedChartCapacityErrorSchema>
export type QuestionHistoryEntryResponseSchemaType = z.infer<typeof schemas.QuestionHistoryEntryResponseSchema>
export type RecordQuestionHistoryRequestDTOType = z.infer<typeof schemas.RecordQuestionHistoryRequestDTO>
export type QuestionOptionDTOType = z.infer<typeof schemas.QuestionOptionDTO>
export type ConditionValueMatcherDTOType = z.infer<typeof schemas.ConditionValueMatcherDTO>
export type QuestionnaireConditionLeafDTOType = z.infer<typeof schemas.QuestionnaireConditionLeafDTO>
export type QuestionnaireConditionInnerGroupDTOType = z.infer<typeof schemas.QuestionnaireConditionInnerGroupDTO>
export type QuestionnaireConditionMiddleGroupDTOType = z.infer<typeof schemas.QuestionnaireConditionMiddleGroupDTO>
export type QuestionnaireConditionOuterGroupDTOType = z.infer<typeof schemas.QuestionnaireConditionOuterGroupDTO>
export type QuestionnaireConditionDTOType = z.infer<typeof schemas.QuestionnaireConditionDTO>
export type QuestionDefaultDTOType = z.infer<typeof schemas.QuestionDefaultDTO>
export type QuestionDefaultValueType = z.infer<typeof schemas.QuestionDefaultValue>
export type QuestionDefinitionDTOType = z.infer<typeof schemas.QuestionDefinitionDTO>
export type QuestionnairePageDTOType = z.infer<typeof schemas.QuestionnairePageDTO>
export type EligibilityQuestionnaireDTOType = z.infer<typeof schemas.EligibilityQuestionnaireDTO>
export type EditEligibilityQuestionnaireRequestDTOType = z.infer<typeof schemas.EditEligibilityQuestionnaireRequestDTO>
export type EditEligibilityQuestionnaireResponseDTOType = z.infer<typeof schemas.EditEligibilityQuestionnaireResponseDTO>
export type OrderStatusCountSchemaType = z.infer<typeof schemas.OrderStatusCountSchema>
export type AggregatedDataSchemaType = z.infer<typeof schemas.AggregatedDataSchema>
export type GetAggregatedDataSchemaType = z.infer<typeof schemas.GetAggregatedDataSchema>
export type OrdersByStateItemSchemaType = z.infer<typeof schemas.OrdersByStateItemSchema>
export type GetOrdersByStateSchemaType = z.infer<typeof schemas.GetOrdersByStateSchema>
export type days_threshold_turnaroundType = z.infer<typeof schemas.days_threshold_turnaround>
export type percentileType = z.infer<typeof schemas.percentile>
export type TurnaroundTimeSchemaType = z.infer<typeof schemas.TurnaroundTimeSchema>
export type LabProcessingTimeSchemaType = z.infer<typeof schemas.LabProcessingTimeSchema>
export type TimeMetricsSchemaType = z.infer<typeof schemas.TimeMetricsSchema>
export type MostRecentOrderSortByInfoDTOType = z.infer<typeof schemas.MostRecentOrderSortByInfoDTO>
export type MostRecentOrderShippingDTOType = z.infer<typeof schemas.MostRecentOrderShippingDTO>
export type MostRecentOrderFulfillmentDTOType = z.infer<typeof schemas.MostRecentOrderFulfillmentDTO>
export type MostRecentOrderPatientInfoDTOType = z.infer<typeof schemas.MostRecentOrderPatientInfoDTO>
export type OrderSchemaType = z.infer<typeof schemas.OrderSchema>
export type OrderPaginatedSchemaType = z.infer<typeof schemas.OrderPaginatedSchema>
export type RecentOrdersSearchPairDTOType = z.infer<typeof schemas.RecentOrdersSearchPairDTO>
export type RecentOrdersSearchRequestDTOType = z.infer<typeof schemas.RecentOrdersSearchRequestDTO>
export type CorrectionBatchDTOType = z.infer<typeof schemas.CorrectionBatchDTO>
export type RequestMultipleTestOrderCorrectionsDTOType = z.infer<typeof schemas.RequestMultipleTestOrderCorrectionsDTO>
export type CreatedTestOrderCorrectionSchemaType = z.infer<typeof schemas.CreatedTestOrderCorrectionSchema>
export type RequestMultipleTestOrderCorrectionsSchemaType = z.infer<typeof schemas.RequestMultipleTestOrderCorrectionsSchema>
export type SetCompletionStatusDTOType = z.infer<typeof schemas.SetCompletionStatusDTO>
export type BatchClientOrderPCPInfoRequestDTOType = z.infer<typeof schemas.BatchClientOrderPCPInfoRequestDTO>
export type PortalBatchPCPInfoSchemaType = z.infer<typeof schemas.PortalBatchPCPInfoSchema>
export type BatchPCPInfoErrorReasonType = z.infer<typeof schemas.BatchPCPInfoErrorReason>
export type BatchPCPInfoErrorSchemaType = z.infer<typeof schemas.BatchPCPInfoErrorSchema>
export type BatchPCPInfoMetaSchemaType = z.infer<typeof schemas.BatchPCPInfoMetaSchema>
export type BatchClientOrderPCPInfoResponseSchemaType = z.infer<typeof schemas.BatchClientOrderPCPInfoResponseSchema>
export type QuestionnaireDTOType = z.infer<typeof schemas.QuestionnaireDTO>
export type ProgramAnalyticsMetricType = z.infer<typeof schemas.ProgramAnalyticsMetric>
export type ProgramAnalyticsRequestDTOType = z.infer<typeof schemas.ProgramAnalyticsRequestDTO>
export type ResultRateStatusType = z.infer<typeof schemas.ResultRateStatus>
export type ProgramAnalyticsSummarySchemaType = z.infer<typeof schemas.ProgramAnalyticsSummarySchema>
export type ProgramAnalyticsProgramSchemaType = z.infer<typeof schemas.ProgramAnalyticsProgramSchema>
export type ProgramAnalyticsResponseSchemaType = z.infer<typeof schemas.ProgramAnalyticsResponseSchema>
export type Body_ask_question_v3_tools_ai_agent_question_postType = z.infer<typeof schemas.Body_ask_question_v3_tools_ai_agent_question_post>
export type DevIdentityFromJwtRequestDTOType = z.infer<typeof schemas.DevIdentityFromJwtRequestDTO>
export type UserTypeType = z.infer<typeof schemas.UserType>
export type DevIdentityScopeDTOType = z.infer<typeof schemas.DevIdentityScopeDTO>
export type DevIdentityFromJwtResponseDTOType = z.infer<typeof schemas.DevIdentityFromJwtResponseDTO>
export type DashboardAggregationPeriodsType = z.infer<typeof schemas.DashboardAggregationPeriods>
export type FileDispositionType = z.infer<typeof schemas.FileDisposition>
export type PeriodNameType = z.infer<typeof schemas.PeriodName>
export type RejectionTypeForAggregationType = z.infer<typeof schemas.RejectionTypeForAggregation>

export const api = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options)
}
